/* embeeLAB 3D-Konfigurator – ausgelagert aus sections/el-konfigurator.liquid (S3, 11.10.2026).
   Braucht vorher: three.js r128 (+ Loader, OrbitControls) und window.EMBEELAB_KONF aus der Section. */
'use strict';

// ============================================================
// ⚙️  KONFIGURATION - Preise & Faktoren hier anpassen
// ============================================================
const CONFIG = {
  // Wandstärke für Shell-Kalkulation (3 Perimeter + Top/Bottom-Layer)
  wallThickness_cm:      0.12,    // 3 Wände × 0.4 mm Düse = 1.2 mm Wandstärke

  // Maschinenkosten
  volumetricFlow_cm3h:  15.00,    // Basis-Volumenstrom bei 0.20 mm Layer (cm³/h)
  machineRate:           0.55,    // Maschinenkosten gesamt €/h (Strom + Abschreibung)

  // Arbeitskosten
  hourlyRate:           35.00,    // Stundenlohn (€/h)
  minutesPerPlate:      10,       // Rüst- & Nachbereitungszeit pro Druckplatte (Minuten), seit 10.10.2026 (vorher 20)
  get laborPerPlate() { return (this.hourlyRate / 60) * this.minutesPerPlate; },
  platePackingBuffer:    0.20,   // 20 % Puffer für Brim, Stützen, Abstände

  // Aufschläge
  packagingSurcharge:    0.05,    // 5 % Verpackungsaufschlag

  // Druckbett-Abmessungen für Plattenkalkulation
  buildPlate_mm:         { x: 250, y: 250 },  // Bambu Lab X1/P1S nutzbarer Druckbereich
  // Teile über 250 mm gehen auf den Bambu Lab H2S. Gerechnet wird bewusst mit 300 × 300 mm,
  // weil wir größer (noch) nicht drucken, auch wenn die H2S-Platte größer ist.
  largePlate_mm:         { x: 300, y: 300 },
  partGap_mm:            5,                   // Mindestabstand zwischen Teilen auf der Platte
  extraPlateDiscount:    0.20,                // 20 % Rabatt auf Rüstzeit ab 2. Platte
  thirdPlateDiscount:    0.25,                // 25 % Rabatt auf Rüstzeit ab 3. Platte
};

// Höchstzahl Dateien pro Auftrag (S4)
const KF_MAX_FILES = 8;

// Druckplatte passend zur Teilgröße: bis 250 mm X1/P1S, darüber H2S
function plateFor(dims) {
  const big = Math.max(dims.x, dims.y, dims.z) > CONFIG.buildPlate_mm.x;
  return big ? CONFIG.largePlate_mm : CONFIG.buildPlate_mm;
}

// Versandkosten werden individuell im Checkout berechnet; ab Schwellenwert kostenlos
const SHIPPING_COST           = 0;     // Nicht mehr fix - wird nicht in die Kalkulation eingerechnet
const FREE_SHIPPING_THRESHOLD = 100.00; // Ab diesem Bestellwert (Zwischensumme) Versand kostenlos

// Absoluter Mindestpreis pro Stück
const MIN_ABSOLUTE_PRICE = 3.00;


// Express-Gebühr: plattenbasiert gestaffelt
// numPlates = Summe aller Druckplatten des Auftrags
function getExpressFee(numPlates) {
  if (numPlates <= 1)  return  9.99;
  if (numPlates <= 2)  return 14.99;
  if (numPlates <= 3)  return 19.99;
  if (numPlates <= 5)  return 27.99;
  if (numPlates <= 10) return 39.99;
  return 59.99;
}

// Mengenabhängige Margen-Staffel (Divisor = 1 − Marge)
const MARGIN_TIERS = [
  { maxQty: 1,        divisor: 0.62 },   // ~38 % VK-Marge
  { maxQty: 5,        divisor: 0.65 },   // ~35 % VK-Marge
  { maxQty: 25,       divisor: 0.70 },   // ~30 % VK-Marge
  { maxQty: Infinity, divisor: 0.75 },   // ~25 % VK-Marge
];

// Komplexitätszuschlag (Oberfläche/Volumen-Verhältnis)
const COMPLEXITY_THRESHOLDS = [
  { minRatio: 10.0, surchargePercent: 0.15 },
  { minRatio:  6.0, surchargePercent: 0.10 },
];

// Staffel-Ersparnis-Cap je Mengenstufe (max. angezeigter Rabatt in %)
const SAVINGS_CAPS = { 1: 0, 2: 30, 3: 37.5, 5: 45, 10: 50, 25: 52.5, 50: 55 };
function getSavingsCap(qty) {
  // Nächst-niedrigere Stufe finden
  const tiers = [50, 25, 10, 5, 3, 2, 1];
  for (const t of tiers) { if (qty >= t) return SAVINGS_CAPS[t]; }
  return 0;
}

// Großmengen ab 100 Stück → individuelles Angebot (kein automatischer Rabatt)
function getBulkDiscount(qty) { return 0; }

// ============================================================
// 📦  MATERIAL-LISTE
// ============================================================
const MATERIALS = [
  { id: 'pla', use: 'Deko &amp; Innenräume',      name: 'PLA / PLA+', desc: 'Allrounder - wir verwenden je nach Verfügbarkeit', density: 1.24, pricePerGram: 0.0200,
    pros: ['Günstig & einfach zu drucken', 'Gute Optik & Maßhaltigkeit', 'Viele Farben verfügbar', 'PLA+ wird ohne Aufpreis verwendet'],
    cons: ['Spröde bei Stößen', 'Nicht für Außenbereich', 'Geringe Wärmebeständigkeit (~60°C)'] },
  { id: 'petg', use: 'Bad, Küche, Feuchtigkeit',     name: 'PETG',       desc: 'Feuchtigkeitsfest, chemikalienbeständig', density: 1.27, pricePerGram: 0.0220,
    pros: ['Feuchtigkeitsfest', 'Chemikalienbeständig', 'Gute Schlagfestigkeit'],
    cons: ['Neigt zu Stringing', 'Weichere Oberfläche', 'Schwieriger als PLA'] },
  { id: 'abs', use: 'Wärme &amp; Schlag',      name: 'ABS',        desc: 'Hohe Temperaturbeständigkeit',            density: 1.04, pricePerGram: 0.0220,
    pros: ['Bis ~105°C hitzestabil', 'Gut nachschleifbar', 'Langlebig & zäh'],
    cons: ['Warping-anfällig', 'Geruch beim Druck', 'Braucht geschlossenen Drucker'] },
  { id: 'asa', use: 'Draußen, Sonne, Auto',      name: 'ASA',        desc: 'UV- & witterungsbeständig, Outdoor',      density: 1.07, pricePerGram: 0.0220,
    pros: ['UV- & witterungsbeständig', 'Perfekt für Außenanwendungen', 'Hitzestabiler als ABS'],
    cons: ['Ähnlich schwierig wie ABS', 'Dämpfe beim Druck', 'Teurer als PLA/PETG'] },
  { id: 'tpu', use: 'Flexibel &amp; stoßfest',      name: 'TPU',        desc: 'Elastisch, stoßfest, gummiartig',         density: 1.21, pricePerGram: 0.0500,
    pros: ['Flexibel & gummiartig', 'Sehr stoßfest', 'Abriebbeständig'],
    cons: ['Deutlich teurer', 'Langsamer Druckprozess', 'Schwieriger zu verarbeiten'] },
];

// ============================================================
// 🎨  FARB-LISTE - wird aus Shop-Metafield embeelab.colors geladen
// Bearbeiten in: Shopify Admin → Einstellungen → Custom Data → Shop
// ============================================================
const PRINT_COLORS = (function() {
  // Kommt aus der Section (Shop-Metafeld embeelab.colors), siehe window.EMBEELAB_KONF in el-konfigurator.liquid
  const aus = window.EMBEELAB_KONF && Array.isArray(window.EMBEELAB_KONF.colors) ? window.EMBEELAB_KONF.colors : [];
  if (aus.length) return aus;
  return [
    { id: 'weiss',   name: 'Weiß',    hex: '#f0f0f0', light: true,  verfuegbar: ['pla','petg','abs','asa','tpu'], aktiv: true },
    { id: 'schwarz', name: 'Schwarz', hex: '#1a1a1a', light: false, verfuegbar: ['pla','petg','abs','asa','tpu'], aktiv: true }
  ];
})();

// ============================================================
// 📐  SCHICHTDICKEN
// ============================================================
const LAYER_HEIGHTS = [
  // ── Ø 0.2 mm Düse (nur PLA / PLA+) ──────────────────────────
  { value: 0.08, label: '0.08 mm', desc: 'Ultra-fein · Höchste Qualität', timeFactor: 2.50, nozzle: 0.2, default: false },
  { value: 0.10, label: '0.10 mm', desc: 'Superfein · Empfohlen',         timeFactor: 2.00, nozzle: 0.2, default: true  },
  { value: 0.12, label: '0.12 mm', desc: 'Sehr fein · Hohe Qualität',     timeFactor: 1.67, nozzle: 0.2, default: false },
  { value: 0.14, label: '0.14 mm', desc: 'Fein · Gute Qualität',          timeFactor: 1.43, nozzle: 0.2, default: false },
  // ── Ø 0.4 mm Düse (alle Materialien) ─────────────────────────
  { value: 0.08, label: '0.08 mm', desc: 'Ultra-fein\nHöchste Qualität', timeFactor: 2.50, nozzle: 0.4, default: false },
  { value: 0.12, label: '0.12 mm', desc: 'Fein\nHohe Qualität',          timeFactor: 1.67, nozzle: 0.4, default: false },
  { value: 0.16, label: '0.16 mm', desc: 'Fein\nGute Qualität',          timeFactor: 1.25, nozzle: 0.4, default: false },
  { value: 0.20, label: '0.20 mm', desc: 'Standard\nEmpfohlen',          timeFactor: 1.00, nozzle: 0.4, default: true  },
  { value: 0.24, label: '0.24 mm', desc: 'Schnell\nGuter Wert',          timeFactor: 0.85, nozzle: 0.4, default: false },
  { value: 0.28, label: '0.28 mm', desc: 'Sehr schnell\nGrobe Details',  timeFactor: 0.72, nozzle: 0.4, default: false },
];

// ============================================================
// 📬  SUBMIT-ENDPUNKT - Shopify nativer Contact-Handler
// POST an /contact → geht direkt an die Store-E-Mail im Shopify Admin
// Einstellungen: Shopify Admin → Einstellungen → Allgemein → Kontakt-E-Mail
// ============================================================


// ============================================================
// STATE
// ============================================================
const state = {
  file:            null,
  fileName:        '',
  fileSize:        0,
  isStepFile:      false,
  fileDataB64:     null,
  originalDims:         { x: 0, y: 0, z: 0 },
  currentDims:          { x: 0, y: 0, z: 0 },
  meshVolume_mm3:       0,     // Echtes Mesh-Volumen (Divergenzsatz), mm³
  meshSurfaceArea_mm2:  0,     // Mesh-Oberfläche, mm²
  hasOverhangs:         false, // Überhang-Erkennung (Stützstruktur-Zuschlag)
  propLocked:         true,
  material:        null,
  color:           PRINT_COLORS[0],
  nozzle:          0.4,
  layerHeight:     LAYER_HEIGHTS.find(lh => lh.nozzle === 0.4 && lh.default),
  infill:          15,
  wallCount:       2,
  quantity:        1,
  expressEnabled:  false,
  comment:         '',
  calc:            { weight: 0, unitPrice: 0, subtotal: 0, shipping: 0, total: 0, expressCharge: 0 },
};

// Three.js globals
let threeScene, threeCamera, threeRenderer, threeControls, threeMesh;
let threeInitialized = false;
let defaultCamPos = { x: 0, y: 0, z: 0 };

// ============================================================
// MULTI-FILE STATE
// ============================================================
let fileList          = [];   // [{ id, file, qty, isStep, originalDims, currentDims, meshVol_mm3, surfArea_mm2, hasOverhangs, fileDataB64 }]
let nextFileId        = 0;
let currentlyLoadingId = -1; // which fileList entry is being loaded into viewer
let activeViewId      = -1;  // which file is currently shown in the 3D viewer
let activeDimsEntryId = -1;  // which file's dims are shown in Schritt 2

// HTML-Sonderzeichen escapen (XSS-Schutz fuer Dateinamen)
function esc(str) {
  return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

function mkFileEntry(file) {
  const ext = file.name.split('.').pop().toLowerCase();
  return {
    id:           nextFileId++,
    file,
    qty:          1,
    isStep:       ext === 'step' || ext === 'stp',
    meshLoaded:   false,
    originalDims: { x: 0, y: 0, z: 0 },
    currentDims:  { x: 0, y: 0, z: 0 },
    meshVol_mm3:  0,
    surfArea_mm2: 0,
    hasOverhangs: false,
    fileDataB64:  null,
  };
}

// ── Per-File Dims Helpers ─────────────────────────────────────
function saveDimsToActiveEntry() {
  const entry = fileList.find(f => f.id === activeDimsEntryId);
  if (entry) entry.currentDims = { ...state.currentDims };
}

function loadDimsFromEntry(entry) {
  if (!entry) return;
  state.originalDims        = { ...entry.originalDims };
  state.currentDims         = { ...entry.currentDims };
  state.meshVolume_mm3      = entry.meshVol_mm3;
  state.meshSurfaceArea_mm2 = entry.surfArea_mm2;
  state.hasOverhangs        = entry.hasOverhangs;
  updateDimInputs();
  // Slider-Max für diese Datei
  const maxOrig = Math.max(entry.originalDims.x, entry.originalDims.y, entry.originalDims.z);
  const maxPct  = maxOrig > 0 ? Math.min(Math.floor(MAX_BUILD_DIM / maxOrig * 100), 500) : 200;
  const slider  = document.getElementById('scale-slider');
  slider.max    = maxPct;
  document.getElementById('scale-max-hint').textContent    = maxPct + ' %';
  document.getElementById('dim-error').style.display       = 'none';
  // Aktuellen Skalierungsprozentsatz wiederherstellen
  let pct = 100;
  if (entry.originalDims.x > 0) pct = Math.round(entry.currentDims.x / entry.originalDims.x * 100);
  pct = Math.min(Math.max(pct, 1), maxPct);
  slider.value = pct;
  document.getElementById('scale-pct-display').textContent = pct + ' %';
  updateSliderBg(slider);
}

function refreshDimsTabs() {
  const container = document.getElementById('dims-file-tabs');
  if (!container) return;
  if (fileList.length <= 1) {
    container.setAttribute('hidden', '');
    if (fileList.length === 1) activeDimsEntryId = fileList[0].id;
    return;
  }
  // Ensure activeDimsEntryId is valid
  if (activeDimsEntryId < 0 || !fileList.find(f => f.id === activeDimsEntryId)) {
    activeDimsEntryId = fileList[0].id;
  }
  container.removeAttribute('hidden');
  container.innerHTML = fileList.map(entry => {
    const name   = esc(entry.file.name.length > 18 ? entry.file.name.substring(0, 16) + '…' : entry.file.name);
    const active = entry.id === activeDimsEntryId;
    return `<button class="kf-dims-tab${active ? ' active' : ''}" data-entry-id="${entry.id}">${name}</button>`;
  }).join('');
  container.querySelectorAll('.kf-dims-tab').forEach(btn => {
    btn.addEventListener('click', () => {
      const newId = parseInt(btn.dataset.entryId);
      if (newId === activeDimsEntryId) return;
      saveDimsToActiveEntry();
      activeDimsEntryId = newId;
      const entry = fileList.find(f => f.id === newId);
      loadDimsFromEntry(entry);
      refreshDimsTabs();
      // Viewer + Dateiliste synchron umschalten
      if (!entry.isStep || entry.meshLoaded) {
        activeViewId = newId;
        loadEntryIntoViewer(entry);
        const container = document.getElementById('file-list');
        container.querySelectorAll('.kf-file-card').forEach(c => c.classList.remove('kf-card-active'));
        container.querySelectorAll('.kf-file-card-eye').forEach(b => b.classList.remove('active'));
        const eyeBtn = container.querySelector(`.kf-file-card-eye[data-id="${newId}"]`);
        if (eyeBtn) { eyeBtn.classList.add('active'); eyeBtn.closest('.kf-file-card').classList.add('kf-card-active'); }
      }
      updatePrice();
    });
  });
}

// (Multi-file Staffel-Logik entfernt - nur noch Single-File Staffelpreise über updateStaffelTable)


function updatePriceSettingsSummary() {
  const el = document.getElementById('price-settings-summary');
  if (!el) return;
  if (!state.material || !state.layerHeight) { el.setAttribute('hidden', ''); return; }
  el.removeAttribute('hidden');
  el.innerHTML =
    `<div class="kf-ps-item"><span class="kf-ps-label">Material</span><span class="kf-ps-val">${state.material.name}</span></div>` +
    `<div class="kf-ps-item"><span class="kf-ps-label">Farbe</span><span class="kf-ps-val"><span class="kf-color-dot" style="background:${state.color.hex}"></span>${state.color.name}</span></div>` +
    `<div class="kf-ps-item"><span class="kf-ps-label">Schichtdicke</span><span class="kf-ps-val">${state.layerHeight.label}</span></div>` +
    `<div class="kf-ps-item"><span class="kf-ps-label">Düse</span><span class="kf-ps-val">Ø ${state.nozzle} mm</span></div>` +
    `<div class="kf-ps-item"><span class="kf-ps-label">Wände</span><span class="kf-ps-val">${state.wallCount}×</span></div>`;
}
// ─────────────────────────────────────────────────────────────

function renderFileList() {
  const container = document.getElementById('file-list');
  const addBtn    = document.getElementById('add-more-btn');
  container.innerHTML = '';

  fileList.forEach(entry => {
    const card = document.createElement('div');
    card.className = 'kf-file-card';
    card.dataset.id = entry.id;
    card.innerHTML = `
      <div class="kf-file-card-icon">
        <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
      </div>
      <div class="kf-file-card-info">
        <div class="kf-file-card-name" title="${esc(entry.file.name)}">${esc(entry.file.name)}</div>
        <div class="kf-file-card-size">${formatFileSize(entry.file.size)}${entry.isStep ? ' · STEP' : ' · STL'}</div>
      </div>
      <div class="kf-file-card-qty">
        <button class="kf-fqty-btn" data-action="minus" data-id="${entry.id}">−</button>
        <div class="kf-fqty-val" id="fqty-val-${entry.id}">${entry.qty}</div>
        <button class="kf-fqty-btn" data-action="plus"  data-id="${entry.id}">+</button>
      </div>
      ${(!entry.isStep || entry.meshLoaded) ? `<button class="kf-file-card-eye" data-id="${entry.id}" title="3D-Vorschau anzeigen">
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
      </button>` : ''}
      <button class="kf-file-card-remove" data-id="${entry.id}" title="Datei entfernen">✕</button>
    `;
    container.appendChild(card);
  });

  // Qty buttons
  container.querySelectorAll('.kf-fqty-btn').forEach(btn => {
    btn.addEventListener('click', e => {
      e.stopPropagation();
      const id    = parseInt(btn.dataset.id);
      const entry = fileList.find(f => f.id === id);
      if (!entry) return;
      entry.qty = btn.dataset.action === 'plus'
        ? Math.min(99, entry.qty + 1)
        : Math.max(1, entry.qty - 1);
      document.getElementById('fqty-val-' + id).textContent = entry.qty;
      // Sync to state.quantity if this is the active/only file
      if (fileList.length === 1) state.quantity = entry.qty;
      updatePrice();
    });
  });

  // Eye buttons - switch viewer to this file
  container.querySelectorAll('.kf-file-card-eye').forEach(btn => {
    btn.addEventListener('click', e => {
      e.stopPropagation();
      const id    = parseInt(btn.dataset.id);
      const entry = fileList.find(f => f.id === id);
      if (!entry) return;
      // Zuerst aktuelle Maße speichern, BEVOR syncActiveEntryToState state überschreibt
      saveDimsToActiveEntry();
      activeViewId = id;
      activeDimsEntryId = id;
      loadEntryIntoViewer(entry);
      syncActiveEntryToState();
      // Maße der neuen Datei laden (nach syncActiveEntryToState, damit es nicht überschrieben wird)
      loadDimsFromEntry(entry);
      refreshDimsTabs();
      // Update active styling
      container.querySelectorAll('.kf-file-card').forEach(c => c.classList.remove('kf-card-active'));
      container.querySelectorAll('.kf-file-card-eye').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      btn.closest('.kf-file-card').classList.add('kf-card-active');
    });
    // Mark currently active viewer
    if (parseInt(btn.dataset.id) === activeViewId) {
      btn.classList.add('active');
      btn.closest('.kf-file-card').classList.add('kf-card-active');
    }
  });

  // Remove buttons
  container.querySelectorAll('.kf-file-card-remove').forEach(btn => {
    btn.addEventListener('click', e => {
      e.stopPropagation();
      const id = parseInt(btn.dataset.id);
      fileList = fileList.filter(f => f.id !== id);
      if (fileList.length === 0) {
        resetFile();
      } else {
        renderFileList();
        // Reload viewer with first remaining file
        const firstViewable = fileList[0];
        if (firstViewable) { activeViewId = firstViewable.id; loadEntryIntoViewer(firstViewable); }
        syncActiveEntryToState();
        updatePrice();
      }
    });
  });

  addBtn.style.display = fileList.length > 0 && fileList.length < KF_MAX_FILES ? 'block' : 'none';
  const multiHint = document.getElementById('multi-hint');
  if (multiHint) multiHint.hidden = fileList.length < 2;
  refreshDimsTabs();
}

function syncActiveEntryToState() {
  // Use first entry as "active" for dims display (step 2)
  const entry = fileList[0];
  if (!entry) return;
  state.file        = entry.file;
  state.fileName    = entry.file.name;
  state.fileSize    = entry.file.size;
  state.isStepFile  = entry.isStep && !entry.meshLoaded;
  state.originalDims = { ...entry.originalDims };
  state.currentDims  = { ...entry.currentDims };
  state.meshVolume_mm3      = entry.meshVol_mm3;
  state.meshSurfaceArea_mm2 = entry.surfArea_mm2;
  state.hasOverhangs        = entry.hasOverhangs;
  state.quantity            = fileList.length === 1 ? entry.qty : 1;
}

function loadEntryIntoViewer(entry) {
  document.getElementById('step-notice').setAttribute('hidden', '');
  document.getElementById('viewer-wrap').setAttribute('hidden', '');

  currentlyLoadingId = entry.id;
  const reader = new FileReader();
  reader.onload = e => {
    document.getElementById('viewer-wrap').removeAttribute('hidden');
    const buf = e.target.result;
    if (entry.isStep) {
      // Zeige Lade-Platzhalter während OCCT die STEP-Datei verarbeitet
      document.getElementById('three-canvas').style.display = 'none';
      document.getElementById('reset-cam-btn').style.display = 'none';
      document.querySelector('.kf-viewer-hint').style.display = 'none';
      document.getElementById('step-placeholder').removeAttribute('hidden');
      if (!threeInitialized) { initThree(); setTimeout(() => loadSTEP(buf), 50); }
      else loadSTEP(buf);
    } else {
      document.getElementById('three-canvas').style.display = '';
      document.getElementById('reset-cam-btn').style.display = '';
      document.querySelector('.kf-viewer-hint').style.display = '';
      document.getElementById('step-placeholder').setAttribute('hidden', '');
      if (!threeInitialized) { initThree(); setTimeout(() => loadSTL(buf), 50); }
      else loadSTL(buf);
    }
  };
  reader.readAsArrayBuffer(entry.file);
}

// Berechnet partsPerPlate für einen Entry (nesting-aware, inkl. 90°-Rotation)
//
// Hybrid-Heuristik (bestimmt die wahrscheinliche Druckorientierung):
// - Achsen werden sortiert: smin ≤ smid ≤ smax
// - "Turm/Vase"-Modell wenn min ≈ mid (≥0.7) UND max deutlich größer (>1.5×):
//     → Footprint = smin × smid (stehend gedruckt)
// - Sonst (Standard "liegend"):
//     → Footprint = smid × smax (auf größter Fläche liegend)
// Damit erkennen wir flache Teile (z.B. 199×2×249 → liegend, 1 Stück) korrekt.
function getPartsPerPlateForEntry(entry) {
  const dims = entry.currentDims;
  if (!dims || dims.x <= 0 || dims.y <= 0 || dims.z <= 0) return 1;
  const meshVol_cm3 = (entry.meshVol_mm3 || 0) / 1000;
  const plateW = plateFor(dims).x + CONFIG.partGap_mm;
  const plateH = plateFor(dims).y + CONFIG.partGap_mm;
  const gap    = CONFIG.partGap_mm;
  // Achsen sortieren
  const axes = [dims.x, dims.y, dims.z].slice().sort((a,b) => a - b);
  const smin = axes[0], smid = axes[1], smax = axes[2];
  const isTowerLike = (smin / smid) > 0.7 && (smax / smid) > 1.5;
  const footA = isTowerLike ? smin : smid;
  const footB = isTowerLike ? smid : smax;
  const sX = footA + gap;
  const sY = footB + gap;
  const grid0  = Math.floor(plateW / sX) * Math.floor(plateH / sY);
  const grid90 = Math.floor(plateW / sY) * Math.floor(plateH / sX);
  let gridParts = Math.max(1, Math.max(grid0, grid90));
  // Runde Teile (footA ≈ footB): Diagonal-Check - passen 2 Stück über Eck?
  const isRound = footA > 0 && footB > 0 && Math.min(footA, footB) / Math.max(footA, footB) > 0.95;
  if (isRound && gridParts === 1) {
    const W = plateFor(dims).x, H = plateFor(dims).y;
    const dx = W - footA, dy = H - footB;
    if (dx > 0 && dy > 0 && Math.sqrt(dx*dx + dy*dy) >= Math.max(footA, footB) + gap * 2) return 2;
  }
  const boundingVol_cm3 = (smin * smid * smax) / 1000;
  const fillRatio = boundingVol_cm3 > 0 ? Math.min(1, meshVol_cm3 / boundingVol_cm3) : 1;
  // Nesting nur bei flachen Teilen (smin < 40 % der zweitkleinsten Achse)
  const isFlat = smin < 0.4 * smid;
  const nestingFactor = isFlat ? Math.min(1.4, 1 + Math.max(0, 0.5 - fillRatio) * 0.8) : 1.0;
  const ppp  = Math.max(1, Math.round(gridParts * nestingFactor));
  const base = Math.max(1, Math.floor(ppp * 0.9)); // 10% Sicherheitspuffer
  return base;
}

function calculatePriceForEntry(entry, laborOverride = null) {
  // Temporarily swap state to this entry's data, calc, restore
  const saved = {
    currentDims:       { ...state.currentDims },
    originalDims:      { ...state.originalDims },
    meshVolume_mm3:    state.meshVolume_mm3,
    meshSurfaceArea_mm2: state.meshSurfaceArea_mm2,
    hasOverhangs:      state.hasOverhangs,
    isStepFile:        state.isStepFile,
    quantity:          state.quantity,
  };
  state.currentDims       = { ...entry.currentDims };
  state.originalDims      = { ...entry.originalDims };
  state.meshVolume_mm3    = entry.meshVol_mm3;
  state.meshSurfaceArea_mm2 = entry.surfArea_mm2;
  state.hasOverhangs      = entry.hasOverhangs;
  state.isStepFile        = entry.isStep && !entry.meshLoaded;
  state.quantity          = entry.qty;
  const result = calculatePrice(laborOverride);
  // restore
  state.currentDims       = saved.currentDims;
  state.originalDims      = saved.originalDims;
  state.meshVolume_mm3    = saved.meshVolume_mm3;
  state.meshSurfaceArea_mm2 = saved.meshSurfaceArea_mm2;
  state.hasOverhangs      = saved.hasOverhangs;
  state.isStepFile        = saved.isStepFile;
  state.quantity          = saved.quantity;
  return result;
}


// Rüstkosten für n Platten (gleiche Formel wie calculatePrice): Platte 1 voll, Platte 2 −20 %, ab 3 −25 %,
// je zusätzlichem Teil auf der Platte −10 %, mindestens 40 %.
function laborForPlates(numPlates, partsPerPlate) {
  const scaled = Math.max(CONFIG.laborPerPlate * 0.40, CONFIG.laborPerPlate * (1 - (partsPerPlate - 1) * 0.10));
  const p2 = numPlates >= 2 ? 1 : 0;
  const p3 = Math.max(0, numPlates - 2);
  return scaled + p2 * scaled * (1 - CONFIG.extraPlateDiscount) + p3 * scaled * (1 - CONFIG.thirdPlateDiscount);
}

// S4: Mehrere Dateien teilen sich Druckplatten und damit die Rüstkosten.
// Material, Farbe und Qualität sind für alle Dateien gleich, daher dürfen die Teile gemeinsam auf eine Platte.
// Getrennt nach Plattengröße (bis 250 mm normal, darüber H2S). Platzbedarf je Teil = 1 / Teile pro Platte.
// Jede Datei trägt den Anteil der Rüstkosten, der ihrem Platzbedarf entspricht, und zahlt nie mehr als einzeln.
function computeSharedPricing() {
  const priced = fileList.filter(e => (!e.isStep || e.meshLoaded) && e.currentDims.x > 0);
  const groups = {};
  priced.forEach(e => {
    const key = plateFor(e.currentDims) === CONFIG.largePlate_mm ? 'gross' : 'normal';
    (groups[key] = groups[key] || []).push(e);
  });
  const laborShare = {};
  let plates = 0, platesSeparate = 0;
  Object.values(groups).forEach(g => {
    const demand = g.reduce((s, e) => s + e.qty / getPartsPerPlateForEntry(e), 0);
    const n = Math.max(1, Math.ceil(demand - 1e-9));
    const qtySum = g.reduce((s, e) => s + e.qty, 0);
    const labor = laborForPlates(n, qtySum / demand);
    plates += n;
    g.forEach(e => {
      laborShare[e.id] = labor * ((e.qty / getPartsPerPlateForEntry(e)) / demand) / e.qty;
      platesSeparate += Math.ceil(e.qty / getPartsPerPlateForEntry(e));
    });
  });
  let subtotal = 0, subtotalSeparate = 0;
  const results = fileList.map(entry => {
    const alone = calculatePriceForEntry(entry);
    if (alone.isStep || !(entry.id in laborShare)) return { entry, c: alone };
    const shared = calculatePriceForEntry(entry, laborShare[entry.id]);
    const c = shared.subtotal <= alone.subtotal ? shared : alone;
    subtotal += c.subtotal;
    subtotalSeparate += alone.subtotal;
    return { entry, c };
  });
  return { results, plates, platesSeparate, subtotal, savings: Math.max(0, subtotalSeparate - subtotal) };
}

// ============================================================
// INIT: UI aufbauen
// ============================================================
function buildMaterialGrid() {
  const grid = document.getElementById('material-grid');
  grid.innerHTML = MATERIALS.map(mat => {
    const prosHtml = mat.pros.map(p => `<div class="kf-mat-tooltip-item">${p}</div>`).join('');
    return `
    <div class="kf-mat-card" data-mat-id="${mat.id}" role="radio" aria-checked="false" tabindex="0">
      <div class="kf-mat-radio">
        <input type="radio" name="material" value="${mat.id}" aria-hidden="true">
        <div class="kf-mat-dot"></div>
      </div>
      <div class="kf-mat-info">
        <div class="kf-mat-name">${mat.name}</div>
        <div class="kf-mat-use">${mat.use}</div>
      </div>
      <div class="kf-mat-tip-wrap" onclick="event.stopPropagation()">
        <button class="kf-mat-tip-btn" aria-label="Material-Info: ${mat.name}" tabindex="-1">?</button>
        <div class="kf-mat-tooltip">
          <div class="kf-mat-tooltip-section kf-mat-tooltip-pros">
            <div class="kf-mat-tooltip-label" style="color:var(--el-accent)">Vorteile</div>
            ${prosHtml}
          </div>
        </div>
      </div>
    </div>`;
  }).join('');
  grid.querySelectorAll('.kf-mat-card').forEach(card => {
    card.addEventListener('click', () => selectMaterial(card.dataset.matId));
    card.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') selectMaterial(card.dataset.matId); });
  });
}

function buildColorGrid() {
  const grid = document.getElementById('color-grid');
  // Filtern nach aktuellem Material - nur Farben anzeigen, die für das Material verfügbar sind
  const matId = (state.material && state.material.id) || 'pla';
  const available = PRINT_COLORS.filter(c => Array.isArray(c.verfuegbar) && c.verfuegbar.includes(matId));
  // Erste aktive Farbe als Default-Auswahl
  let firstActiveIdx = -1;
  available.forEach((c, i) => { if (c.aktiv && firstActiveIdx === -1) firstActiveIdx = i; });
  if (firstActiveIdx === -1) firstActiveIdx = 0;
  grid.innerHTML = available.map((c, i) => {
    const isSelected = i === firstActiveIdx;
    const isTransparent = c.hex === 'transparent';
    const bg = isTransparent
      ? 'background: repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 0 0 / 8px 8px;'
      : `background:${c.hex};`;
    const disabled = !c.aktiv;
    return `
    <div class="kf-color-swatch${isSelected ? ' selected' : ''}${disabled ? ' kf-color-swatch--disabled' : ''}"
         data-color-id="${c.id}"
         data-disabled="${disabled}"
         style="${bg}"
         title="${c.name}${disabled ? ' - aktuell nicht verfügbar' : ''}"
         role="radio"
         aria-label="${c.name}${disabled ? ' (aktuell nicht verfügbar)' : ''}"
         aria-checked="${isSelected}"
         aria-disabled="${disabled}"
         tabindex="${disabled ? -1 : 0}">
    </div>`;
  }).join('');
  grid.querySelectorAll('.kf-color-swatch').forEach(sw => {
    if (sw.dataset.disabled === 'true') return;
    sw.addEventListener('click', () => selectColor(sw.dataset.colorId));
    sw.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') selectColor(sw.dataset.colorId); });
  });
  // State sync: falls aktuelle Farbe nicht mehr verfügbar/aktiv ist, auf erste verfügbare wechseln
  const stillThere = available.find(c => c.id === (state.color && state.color.id) && c.aktiv);
  if (!stillThere && available[firstActiveIdx]) {
    state.color = available[firstActiveIdx];
  }
}

function rebuildLayerSlider() {
  const available = LAYER_HEIGHTS.filter(lh => lh.nozzle === state.nozzle);
  const defIdx    = available.findIndex(lh => lh.default);
  const useIdx    = defIdx >= 0 ? defIdx : 0;

  document.getElementById('layer-grid').innerHTML = `
    <div class="kf-layer-info">
      <span class="kf-layer-current-val" id="layer-current-val">-</span>
      <span class="kf-layer-current-desc" id="layer-current-desc">-</span>
    </div>
    <input type="range" class="kf-slider" id="layer-slider"
           min="0" max="${available.length - 1}" step="1" value="${useIdx}"
           aria-label="Schichtdicke">
    <div class="kf-layer-ticks">
      ${available.map((lh, i) => `<span data-idx="${i}">${lh.label}</span>`).join('')}
    </div>
  `;

  const slider = document.getElementById('layer-slider');

  function applyLayer(idx) {
    const lh = available[idx];
    state.layerHeight = lh;
    slider.value = idx;
    document.getElementById('layer-current-val').textContent  = lh.label;
    document.getElementById('layer-current-desc').textContent = lh.desc.replace('\n', ' · ');
    updateSliderBg(slider);
    // Highlight active tick
    document.querySelectorAll('.kf-layer-ticks span').forEach((s, i) => {
      s.classList.toggle('kf-tick-active', i === idx);
    });
    updatePrice();
  }

  slider.addEventListener('input', function() { applyLayer(parseInt(this.value)); });

  // Tick-Labels klickbar machen
  document.querySelectorAll('.kf-layer-ticks span').forEach(span => {
    span.addEventListener('click', function() { applyLayer(parseInt(this.dataset.idx)); });
  });

  applyLayer(useIdx);
}

function buildLayerGrid() {
  rebuildLayerSlider();

  // Nozzle-Toggle Events
  document.querySelectorAll('.kf-nozzle-btn').forEach(btn => {
    btn.addEventListener('click', function() {
      const nozzle = parseFloat(this.dataset.nozzle);
      if (state.nozzle === nozzle) return;
      state.nozzle = nozzle;
      document.querySelectorAll('.kf-nozzle-btn').forEach(b => b.classList.remove('active'));
      this.classList.add('active');

      // 0.2mm Düse: nur PLA / PLA+ erlaubt
      if (nozzle === 0.2 && state.material && !['pla','pla-plus'].includes(state.material.id)) {
        selectMaterial('pla');
      }
      refreshMaterialGrid();
      rebuildLayerSlider();
    });
  });
}

function refreshMaterialGrid() {
  const restrictTo02 = state.nozzle === 0.2;
  document.querySelectorAll('.kf-mat-card').forEach(card => {
    const id = card.dataset.matId;
    const incompatible = restrictTo02 && !['pla','pla-plus'].includes(id);
    card.classList.toggle('kf-mat-disabled', incompatible);
    card.style.pointerEvents = incompatible ? 'none' : '';
  });
}


// ============================================================
// SELEKTOREN
// ============================================================
function selectMaterial(id) {
  const mat = MATERIALS.find(m => m.id === id);
  if (!mat) return;
  state.material = mat;
  document.querySelectorAll('.kf-mat-card').forEach(c => {
    const selected = c.dataset.matId === id;
    c.classList.toggle('selected', selected);
    c.setAttribute('aria-checked', selected);
  });
  // Farbgrid neu bauen (zeigt nur Farben, die für dieses Material verfügbar sind)
  if (typeof buildColorGrid === 'function') buildColorGrid();
  // PLA-Hinweis nicht mehr nötig (PLA & PLA+ zusammengefasst)
  const notice = document.getElementById('pla-color-notice');
  if (notice) notice.setAttribute('hidden', '');
  updatePrice();
}

function selectColor(id) {
  const col = PRINT_COLORS.find(c => c.id === id);
  if (!col) return;
  state.color = col;
  document.querySelectorAll('.kf-color-swatch').forEach(sw => {
    const selected = sw.dataset.colorId === id;
    sw.classList.toggle('selected', selected);
    sw.setAttribute('aria-checked', selected);
  });
  document.getElementById('color-name-display').textContent = col.name;
  if (threeMesh) {
    if (threeMesh.isGroup) {
      threeMesh.traverse(child => { if (child.isMesh) child.material.color.set(col.hex); });
    } else {
      threeMesh.material.color.set(col.hex);
    }
  }
  updatePrice();
}


// ============================================================
// DREI.JS - INITIALISIERUNG
// ============================================================
function initThree() {
  const canvas = document.getElementById('three-canvas');
  const wrapper = document.getElementById('viewer-wrap');

  threeScene = new THREE.Scene();

  const w = wrapper.offsetWidth  || 640;
  const h = wrapper.offsetHeight || 480;

  threeCamera = new THREE.PerspectiveCamera(45, w / h, 0.01, 1000000);
  threeRenderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  threeRenderer.setClearColor(0x000000, 0);
  threeRenderer.setSize(w, h, false);
  threeRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  const ambientLight = new THREE.AmbientLight(0xffffff, 0.55);
  threeScene.add(ambientLight);
  const mainLight = new THREE.DirectionalLight(0xffffff, 0.9);
  mainLight.position.set(1, 2, 1.5);
  threeScene.add(mainLight);
  const fillLight = new THREE.DirectionalLight(0x9999cc, 0.25);
  fillLight.position.set(-1, -0.5, -1);
  threeScene.add(fillLight);
  const rimLight = new THREE.DirectionalLight(0xfff4cc, 0.15);
  rimLight.position.set(0, -1, 2);
  threeScene.add(rimLight);

  threeControls = new THREE.OrbitControls(threeCamera, threeRenderer.domElement);
  threeControls.enableDamping    = true;
  threeControls.dampingFactor    = 0.08;
  threeControls.minDistance      = 0.01;
  threeControls.maxDistance      = 1000000;
  threeControls.screenSpacePanning = true;

  threeInitialized = true;
  animateThree();

  const resizeObs = new ResizeObserver(() => onThreeResize());
  resizeObs.observe(wrapper);
}

function animateThree() {
  requestAnimationFrame(animateThree);
  if (threeControls) threeControls.update();
  if (threeRenderer && threeScene && threeCamera) {
    threeRenderer.render(threeScene, threeCamera);
  }
}

function onThreeResize() {
  if (!threeRenderer || !threeCamera) return;
  const wrapper = document.getElementById('viewer-wrap');
  const w = wrapper.offsetWidth;
  const h = wrapper.offsetHeight;
  if (w === 0 || h === 0) return;
  threeCamera.aspect = w / h;
  threeCamera.updateProjectionMatrix();
  threeRenderer.setSize(w, h, false);
}


// ============================================================
// GEOMETRIE-STATISTIKEN: echtes Volumen + Oberfläche
// Algorithmus: Divergenzsatz (signed volume über alle Dreiecke)
// Ergebnis: volume in mm³, area in mm²
// ============================================================
function computeGeometryStats(geometry) {
  const geom = geometry.index ? geometry.toNonIndexed() : geometry;
  const pos  = geom.attributes.position;
  let vol = 0, area = 0, overhangArea = 0;
  const v1 = new THREE.Vector3(), v2 = new THREE.Vector3(), v3 = new THREE.Vector3();
  const e1 = new THREE.Vector3(), e2 = new THREE.Vector3(), cr = new THREE.Vector3();
  for (let i = 0; i < pos.count; i += 3) {
    v1.fromBufferAttribute(pos, i);
    v2.fromBufferAttribute(pos, i + 1);
    v3.fromBufferAttribute(pos, i + 2);
    // Signed volume contribution (Divergenzsatz)
    vol += (v1.x * (v2.y * v3.z - v3.y * v2.z)
          + v1.y * (v3.x * v2.z - v2.x * v3.z)
          + v1.z * (v2.x * v3.y - v3.x * v2.y)) / 6;
    // Dreiecksfläche
    e1.subVectors(v2, v1);
    e2.subVectors(v3, v1);
    cr.crossVectors(e1, e2);
    const triArea = cr.length() / 2;
    area += triArea;
    // Überhang-Erkennung: Normale zeigt nach unten (Z-Komponente < −cos 45° ≈ −0.707)
    if (cr.z < 0 && triArea > 0) {
      const normalZ = cr.z / (cr.length() || 1);
      if (normalZ < -0.707) overhangArea += triArea;
    }
  }
  if (geometry.index) geom.dispose();
  return { volume: Math.abs(vol), area, overhangArea };
}


// ============================================================
// PANE1-NAV: stepabhängig ein-/ausblenden
// ============================================================
function refreshPane1Nav() {
  const nav = document.getElementById('pane1-nav');
  const btn = document.getElementById('pane1-weiter-btn');
  if (wizardStep === 1 && fileList.length > 0) {
    btn.textContent = 'Weiter: Maße anpassen →';
    btn.className = 'kf-step-nav-btn kf-step-nav-btn--next';
    btn.style.width = '100%';
    nav.classList.add('kf-show');
  } else if (wizardStep === 6 && fileList.length > 0) {
    btn.textContent = '← Maße nochmal anpassen';
    btn.className = 'kf-step-nav-btn kf-step-nav-btn--back';
    btn.style.width = '100%';
    nav.classList.add('kf-show');
  } else {
    nav.classList.remove('kf-show');
  }
}

// ============================================================
// STL LADEN
// ============================================================
function loadSTL(arrayBuffer) {
  const loader = new THREE.STLLoader();
  let geometry;
  try {
    geometry = loader.parse(arrayBuffer);
  } catch(e) {
    kfError('Die Datei konnte nicht gelesen werden. Bitte prüfe, ob es sich um eine gültige STL-Datei handelt.');
    return;
  }

  geometry.computeBoundingBox();
  const box  = geometry.boundingBox;
  const size = new THREE.Vector3();
  box.getSize(size);

  state.originalDims = { x: size.x, y: size.y, z: size.z };
  state.currentDims  = { ...state.originalDims };

  const stats = computeGeometryStats(geometry);
  state.meshVolume_mm3      = stats.volume;
  state.meshSurfaceArea_mm2 = stats.area;
  state.hasOverhangs        = stats.area > 0 && (stats.overhangArea / stats.area) > 0.05;

  // Write dims back to the fileList entry that triggered this load
  if (currentlyLoadingId >= 0) {
    const loadedEntry = fileList.find(f => f.id === currentlyLoadingId);
    if (loadedEntry) {
      loadedEntry.originalDims = { ...state.originalDims };
      // Bereits angepasste Maße beibehalten (Reload via Auge-Button), nur beim Erstladen setzen
      if (loadedEntry.currentDims.x > 0) {
        state.currentDims = { ...loadedEntry.currentDims };  // user's dims zurück in state
      } else {
        loadedEntry.currentDims = { ...state.currentDims };  // Erstladen: aus Geometrie übernehmen
      }
      loadedEntry.meshVol_mm3  = state.meshVolume_mm3;
      loadedEntry.surfArea_mm2 = state.meshSurfaceArea_mm2;
      loadedEntry.hasOverhangs = state.hasOverhangs;
    }
  }

  geometry.center();

  const mat = new THREE.MeshPhongMaterial({
    color: state.color.hex,
    specular: 0x222222,
    shininess: 25,
    side: THREE.DoubleSide,
  });

  if (threeMesh) {
    threeScene.remove(threeMesh);
    threeMesh.geometry.dispose();
    threeMesh.material.dispose();
  }

  threeMesh = new THREE.Mesh(geometry, mat);
  threeScene.add(threeMesh);

  geometry.computeBoundingSphere();
  const radius = geometry.boundingSphere.radius;
  const fovRad = threeCamera.fov * Math.PI / 180;
  let dist = radius / Math.sin(fovRad / 2) * 1.6;

  threeCamera.near = radius / 100;
  threeCamera.far  = radius * 200;
  threeCamera.updateProjectionMatrix();

  threeCamera.position.set(dist * 0.7, dist * 0.5, dist);
  threeCamera.lookAt(0, 0, 0);
  threeControls.target.set(0, 0, 0);
  threeControls.update();

  defaultCamPos = {
    x: threeCamera.position.x,
    y: threeCamera.position.y,
    z: threeCamera.position.z,
  };

  // Dims-UI + Skalierung wiederherstellen wenn diese Datei die aktive dims-Datei ist
  if (currentlyLoadingId === activeDimsEntryId || activeDimsEntryId < 0) {
    const activeEntry = fileList.find(f => f.id === currentlyLoadingId);
    if (activeEntry && activeEntry.currentDims.x > 0) {
      loadDimsFromEntry(activeEntry);  // stellt korrekte Skalierung wieder her
    } else {
      updateDimInputs();
      initScaleSlider();
    }
  }
  updateMeshScale();  // Viewer-Mesh auf aktuelle Skalierung setzen
  updatePrice();

  document.getElementById('viewer-wrap').removeAttribute('hidden');
  document.getElementById('upload-zone').setAttribute('hidden', '');
  refreshPane1Nav();
}

// ============================================================
// OCCT-IMPORT-JS - lazy loader für STEP-Dateien
// ============================================================
let _occtPromise = null;

function loadOcct() {
  if (_occtPromise) return _occtPromise;
  const VERSION = '0.0.23';
  const DIST    = 'https://cdn.jsdelivr.net/npm/occt-import-js@' + VERSION + '/dist/';
  _occtPromise  = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src   = DIST + 'occt-import-js.js';
    s.onload = async () => {
      try {
        const factory = window.occtimportjs;
        if (typeof factory !== 'function') throw new Error('occtimportjs nicht gefunden');
        const occt = await factory({ locateFile: f => DIST + f });
        resolve(occt);
      } catch(e) {
        _occtPromise = null;
        reject(e);
      }
    };
    s.onerror = () => { _occtPromise = null; reject(new Error('occt-import-js konnte nicht geladen werden')); };
    document.head.appendChild(s);
  });
  return _occtPromise;
}

// ============================================================
// STEP LADEN (via occt-import-js / WebAssembly)
// ============================================================
async function loadSTEP(arrayBuffer) {
  let occt;
  try {
    occt = await loadOcct();
  } catch(e) {
    kfError('Die STEP-Vorschau konnte nicht geladen werden. Bitte prüfe deine Internetverbindung.');
    // Zeige Placeholder dauerhaft
    return;
  }

  // Prüfen ob diese Datei noch die aktive ist (User hat ggf. währenddessen Datei gewechselt)
  const loadingId = currentlyLoadingId;

  let result;
  try {
    result = occt.ReadStepFile(new Uint8Array(arrayBuffer), null);
  } catch(e) {
    kfError('Die STEP-Datei konnte nicht gelesen werden. Bitte prüfe, ob es sich um eine gültige STEP-Datei handelt.');
    return;
  }

  if (!result.success || !result.meshes || result.meshes.length === 0) {
    kfError('Die STEP-Datei enthält keine darstellbaren Geometrien.');
    return;
  }

  try {
    // Alle Meshes in eine gemeinsame nicht-indizierte BufferGeometry zusammenführen
    const posArrays = [];
    const norArrays = [];

    for (let i = 0; i < result.meshes.length; i++) {
      const mesh   = result.meshes[i];
      const pos    = mesh.attributes.position.array;
      const nor    = mesh.attributes.normal ? mesh.attributes.normal.array : null;
      const idx    = mesh.index && mesh.index.array && mesh.index.array.length > 0 ? mesh.index.array : null;

      if (idx) {
        const cnt    = idx.length;
        const expPos = new Float32Array(cnt * 3);
        const expNor = nor ? new Float32Array(cnt * 3) : null;
        for (let j = 0; j < cnt; j++) {
          const vi = idx[j] * 3;
          expPos[j*3]   = pos[vi];   expPos[j*3+1] = pos[vi+1]; expPos[j*3+2] = pos[vi+2];
          if (expNor) { expNor[j*3] = nor[vi]; expNor[j*3+1] = nor[vi+1]; expNor[j*3+2] = nor[vi+2]; }
        }
        posArrays.push(expPos);
        if (expNor) norArrays.push(expNor);
      } else {
        posArrays.push(new Float32Array(pos));
        if (nor) norArrays.push(new Float32Array(nor));
      }
    }

    const totalLen = posArrays.reduce((s, a) => s + a.length, 0);
    if (totalLen === 0) throw new Error('Keine Vertices in der STEP-Datei');

    const merged = new Float32Array(totalLen);
    let off = 0;
    for (const a of posArrays) { merged.set(a, off); off += a.length; }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(merged, 3));

    if (norArrays.length === posArrays.length && norArrays.length > 0) {
      const totalNorLen = norArrays.reduce((s, a) => s + a.length, 0);
      const mergedNor   = new Float32Array(totalNorLen);
      let nOff = 0;
      for (const a of norArrays) { mergedNor.set(a, nOff); nOff += a.length; }
      geometry.setAttribute('normal', new THREE.BufferAttribute(mergedNor, 3));
    } else {
      geometry.computeVertexNormals();
    }

    // Ab hier identisch mit loadSTL
    geometry.computeBoundingBox();
    const box  = geometry.boundingBox;
    const size = new THREE.Vector3();
    box.getSize(size);

    if (!isFinite(size.x) || size.x <= 0) throw new Error('Ungültige Bounding Box');

    state.originalDims = { x: size.x, y: size.y, z: size.z };
    state.currentDims  = { ...state.originalDims };
    state.isStepFile   = false; // echte Mesh-Daten vorhanden

    const stats = computeGeometryStats(geometry);
    state.meshVolume_mm3      = stats.volume;
    state.meshSurfaceArea_mm2 = stats.area;
    state.hasOverhangs        = stats.area > 0 && (stats.overhangArea / stats.area) > 0.05;

    if (loadingId >= 0) {
      const loadedEntry = fileList.find(f => f.id === loadingId);
      if (loadedEntry) {
        loadedEntry.meshLoaded   = true;
        loadedEntry.originalDims = { ...state.originalDims };
        if (loadedEntry.currentDims.x > 0) {
          state.currentDims = { ...loadedEntry.currentDims };
        } else {
          loadedEntry.currentDims = { ...state.currentDims };
        }
        loadedEntry.meshVol_mm3  = state.meshVolume_mm3;
        loadedEntry.surfArea_mm2 = state.meshSurfaceArea_mm2;
        loadedEntry.hasOverhangs = state.hasOverhangs;
      }
    }

    geometry.center();

    const mat = new THREE.MeshPhongMaterial({
      color:     state.color.hex,
      specular:  0x222222,
      shininess: 25,
      side:      THREE.DoubleSide,
    });

    if (threeMesh) {
      threeScene.remove(threeMesh);
      threeMesh.geometry.dispose();
      threeMesh.material.dispose();
    }

    threeMesh = new THREE.Mesh(geometry, mat);
    threeScene.add(threeMesh);

    geometry.computeBoundingSphere();
    const radius = geometry.boundingSphere.radius;
    const fovRad = threeCamera.fov * Math.PI / 180;
    const dist   = radius / Math.sin(fovRad / 2) * 1.6;

    threeCamera.near = radius / 100;
    threeCamera.far  = radius * 200;
    threeCamera.updateProjectionMatrix();
    threeCamera.position.set(dist * 0.7, dist * 0.5, dist);
    threeCamera.lookAt(0, 0, 0);
    threeControls.target.set(0, 0, 0);
    threeControls.update();

    defaultCamPos = { x: threeCamera.position.x, y: threeCamera.position.y, z: threeCamera.position.z };

    // Canvas sichtbar schalten (Placeholder ausblenden)
    document.getElementById('step-placeholder').setAttribute('hidden', '');
    document.getElementById('three-canvas').style.display = '';
    document.getElementById('reset-cam-btn').style.display = '';
    document.querySelector('.kf-viewer-hint').style.display = '';

    // Dims-UI + Skalierung wiederherstellen
    if (loadingId === activeDimsEntryId || activeDimsEntryId < 0) {
      const activeEntry = fileList.find(f => f.id === loadingId);
      if (activeEntry && activeEntry.currentDims.x > 0) {
        loadDimsFromEntry(activeEntry);
      } else {
        updateDimInputs();
        initScaleSlider();
      }
    }

    updateMeshScale();
    updatePrice();

    document.getElementById('viewer-wrap').removeAttribute('hidden');
    document.getElementById('upload-zone').setAttribute('hidden', '');
    renderFileList(); // Auge-Button für STEP-Datei jetzt sichtbar machen
    refreshPane1Nav();

  } catch(e) {
    console.error('STEP geometry error:', e);
    kfError('Die STEP-Datei konnte nicht gerendert werden: ' + e.message);
  }
}

// ============================================================
// OBJ LADEN
// ============================================================
function loadOBJ(text) {
  const loader = new THREE.OBJLoader();
  let group;
  try {
    group = loader.parse(text);
  } catch(e) {
    kfError('Die OBJ-Datei konnte nicht gelesen werden.');
    return;
  }

  const box  = new THREE.Box3().setFromObject(group);
  const size = new THREE.Vector3();
  box.getSize(size);
  state.originalDims = { x: size.x, y: size.y, z: size.z };
  state.currentDims  = { ...state.originalDims };

  let totalVol = 0, totalArea = 0, totalOverhangArea = 0;
  group.traverse(child => {
    if (child.isMesh && child.geometry) {
      const s = computeGeometryStats(child.geometry);
      totalVol         += s.volume;
      totalArea        += s.area;
      totalOverhangArea += s.overhangArea;
    }
  });
  state.meshVolume_mm3      = totalVol;
  state.meshSurfaceArea_mm2 = totalArea;
  state.hasOverhangs        = totalArea > 0 && (totalOverhangArea / totalArea) > 0.05;

  const center = new THREE.Vector3();
  box.getCenter(center);
  group.position.sub(center);

  const mat = new THREE.MeshPhongMaterial({ color: state.color.hex, specular: 0x222222, shininess: 25, side: THREE.DoubleSide });
  group.traverse(child => { if (child.isMesh) child.material = mat.clone(); });

  if (threeMesh) {
    threeScene.remove(threeMesh);
    if (threeMesh.isGroup) {
      threeMesh.traverse(c => { if (c.isMesh) { c.geometry.dispose(); c.material.dispose(); } });
    } else { threeMesh.geometry.dispose(); threeMesh.material.dispose(); }
  }
  threeMesh = group;
  threeScene.add(threeMesh);

  const newBox    = new THREE.Box3().setFromObject(group);
  const sphere    = new THREE.Sphere();
  newBox.getBoundingSphere(sphere);
  const radius    = sphere.radius;
  const fovRad    = threeCamera.fov * Math.PI / 180;
  const dist      = radius / Math.sin(fovRad / 2) * 1.6;
  threeCamera.near = radius / 100;
  threeCamera.far  = radius * 200;
  threeCamera.updateProjectionMatrix();
  threeCamera.position.set(dist * 0.7, dist * 0.5, dist);
  threeCamera.lookAt(0, 0, 0);
  threeControls.target.set(0, 0, 0);
  threeControls.update();
  defaultCamPos = { x: threeCamera.position.x, y: threeCamera.position.y, z: threeCamera.position.z };

  updateDimInputs();
  initScaleSlider();
  updatePrice();
  document.getElementById('viewer-wrap').removeAttribute('hidden');
  document.getElementById('upload-zone').setAttribute('hidden', '');
  refreshPane1Nav();
}


// ============================================================
// PLY LADEN
// ============================================================
function loadPLY(arrayBuffer) {
  const loader = new THREE.PLYLoader();
  let geometry;
  try {
    geometry = loader.parse(arrayBuffer);
  } catch(e) {
    kfError('Die PLY-Datei konnte nicht gelesen werden.');
    return;
  }

  geometry.computeBoundingBox();
  const box  = geometry.boundingBox;
  const size = new THREE.Vector3();
  box.getSize(size);
  state.originalDims = { x: size.x, y: size.y, z: size.z };
  state.currentDims  = { ...state.originalDims };

  const stats = computeGeometryStats(geometry);
  state.meshVolume_mm3      = stats.volume;
  state.meshSurfaceArea_mm2 = stats.area;
  state.hasOverhangs        = stats.area > 0 && (stats.overhangArea / stats.area) > 0.05;

  geometry.center();

  const mat = new THREE.MeshPhongMaterial({ color: state.color.hex, specular: 0x222222, shininess: 25, side: THREE.DoubleSide });

  if (threeMesh) {
    threeScene.remove(threeMesh);
    if (threeMesh.isGroup) {
      threeMesh.traverse(c => { if (c.isMesh) { c.geometry.dispose(); c.material.dispose(); } });
    } else { threeMesh.geometry.dispose(); threeMesh.material.dispose(); }
  }
  threeMesh = new THREE.Mesh(geometry, mat);
  threeScene.add(threeMesh);

  geometry.computeBoundingSphere();
  const radius = geometry.boundingSphere.radius;
  const fovRad = threeCamera.fov * Math.PI / 180;
  const dist   = radius / Math.sin(fovRad / 2) * 1.6;
  threeCamera.near = radius / 100;
  threeCamera.far  = radius * 200;
  threeCamera.updateProjectionMatrix();
  threeCamera.position.set(dist * 0.7, dist * 0.5, dist);
  threeCamera.lookAt(0, 0, 0);
  threeControls.target.set(0, 0, 0);
  threeControls.update();
  defaultCamPos = { x: threeCamera.position.x, y: threeCamera.position.y, z: threeCamera.position.z };

  updateDimInputs();
  initScaleSlider();
  updatePrice();
  document.getElementById('viewer-wrap').removeAttribute('hidden');
  document.getElementById('upload-zone').setAttribute('hidden', '');
  refreshPane1Nav();
}



function updateMeshScale() {
  if (!threeMesh || !state.originalDims.x) return;
  threeMesh.scale.set(
    state.currentDims.x / state.originalDims.x,
    state.currentDims.y / state.originalDims.y,
    state.currentDims.z / state.originalDims.z
  );
}

function updateDimInputs() {
  document.getElementById('dim-x').value = state.currentDims.x.toFixed(1);
  document.getElementById('dim-y').value = state.currentDims.y.toFixed(1);
  document.getElementById('dim-z').value = state.currentDims.z.toFixed(1);
}


// ============================================================
// DATEI-UPLOAD: Event-Handler
// ============================================================
function handleFile(file) {
  if (!file) return;

  const maxSize = 1024 * 1024 * 1024;
  if (file.size > maxSize) {
    kfError('Die Datei ist größer als 1 GB. Bitte verwende eine kleinere Datei.');
    return;
  }

  const ext = file.name.split('.').pop().toLowerCase();
  if (!['stl', 'step', 'stp'].includes(ext)) {
    kfError('Ungültiges Dateiformat. Bitte verwende STL oder STEP.\nFür OBJ/PLY-Dateien nutze bitte das Anfrageformular.');
    return;
  }

  // Duplicate check
  if (fileList.some(f => f.file.name === file.name && f.file.size === file.size)) return;

  if (fileList.length >= KF_MAX_FILES) {
    kfError('Maximal ' + KF_MAX_FILES + ' Dateien pro Auftrag. Für mehr schreib uns über das Anfrageformular.');
    return;
  }
  const entry = mkFileEntry(file);
  fileList.push(entry);
  kfTrack('upload');

  // Hide upload zone + hint after first file
  document.getElementById('upload-zone').setAttribute('hidden', '');
  document.getElementById('bundle-hint').style.display = 'none';

  // Viewer: load latest file (STEP wird via OCCT in 3D gerendert)
  activeViewId = entry.id;
  loadEntryIntoViewer(entry);

  // Kleindateien als Base64 für das Bestellformular kodieren (nur STL, max 5 MB)
  if (!entry.isStep && file.size <= 5 * 1024 * 1024) {
    const b64Reader = new FileReader();
    b64Reader.onload = e2 => { entry.fileDataB64 = e2.target.result.split(',')[1]; };
    b64Reader.readAsDataURL(file);
  }

  // Dims-Tab immer auf die neu hochgeladene Datei setzen (synchron mit Viewer)
  activeDimsEntryId = entry.id;
  renderFileList();
  syncActiveEntryToState();
  updatePrice();
  refreshPane1Nav();
}

function resetFile() {
  fileList = [];
  nextFileId = 0;
  activeDimsEntryId = -1;
  state.file              = null;
  state.fileName          = '';
  state.fileDataB64       = null;
  state.originalDims      = { x: 0, y: 0, z: 0 };
  state.currentDims       = { x: 0, y: 0, z: 0 };
  state.meshVolume_mm3      = 0;
  state.meshSurfaceArea_mm2 = 0;
  state.hasOverhangs        = false;
  state.quantity            = 1;

  document.getElementById('file-input').value = '';
  document.getElementById('file-list').innerHTML = '';
  document.getElementById('add-more-btn').style.display = 'none';
  document.getElementById('step-notice').setAttribute('hidden', '');
  document.getElementById('viewer-wrap').setAttribute('hidden', '');
  document.getElementById('upload-zone').removeAttribute('hidden');
  document.getElementById('bundle-hint').style.display = '';
  document.getElementById('pane1-nav').classList.remove('kf-show');
  goToStep(1);

  if (threeMesh && threeScene) {
    threeScene.remove(threeMesh);
    if (threeMesh.isGroup) {
      threeMesh.traverse(child => {
        if (child.isMesh) { child.geometry.dispose(); child.material.dispose(); }
      });
    } else {
      threeMesh.geometry.dispose();
      threeMesh.material.dispose();
    }
    threeMesh = null;
  }
  updatePrice();
}


// ============================================================
// UPLOAD ZONE: Drag & Drop + Klick
// ============================================================
const uploadZone = document.getElementById('upload-zone');
const fileInput  = document.getElementById('file-input');

uploadZone.addEventListener('click', () => fileInput.click());
uploadZone.addEventListener('keydown', e => {
  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.click(); }
});
uploadZone.addEventListener('dragover', e => {
  e.preventDefault();
  uploadZone.classList.add('drag-over');
});
uploadZone.addEventListener('dragleave', () => uploadZone.classList.remove('drag-over'));
uploadZone.addEventListener('drop', e => {
  e.preventDefault();
  uploadZone.classList.remove('drag-over');
  Array.from(e.dataTransfer.files).slice(0, KF_MAX_FILES).forEach(handleFile);
});
fileInput.addEventListener('change', () => {
  Array.from(fileInput.files).slice(0, KF_MAX_FILES).forEach(handleFile);
  fileInput.value = '';
});

// "Weitere Datei hinzufügen" Button
document.getElementById('add-more-btn').addEventListener('click', () => fileInput.click());
document.getElementById('reset-cam-btn').addEventListener('click', () => {
  if (!threeCamera || !threeControls) return;
  threeCamera.position.set(defaultCamPos.x, defaultCamPos.y, defaultCamPos.z);
  threeControls.target.set(0, 0, 0);
  threeControls.update();
});


// ============================================================
// DIMENSION INPUTS
// ============================================================
const MAX_BUILD_DIM = 300;

['dim-x', 'dim-y', 'dim-z'].forEach(id => {
  const axis = id.split('-')[1];
  document.getElementById(id).addEventListener('change', function() {
    let newVal = parseFloat(this.value);
    if (isNaN(newVal) || newVal <= 0) {
      this.value = state.currentDims[axis].toFixed(1);
      return;
    }
    newVal = Math.min(newVal, MAX_BUILD_DIM);
    const orig = state.originalDims[axis];
    if (orig === 0) {
      // STEP-Datei: direkte Eingabe ohne proportionale Skalierung
      state.currentDims[axis] = newVal;
      saveDimsToActiveEntry();
      updatePrice();
      return;
    }

    const dimError = document.getElementById('dim-error');
    if (state.propLocked) {
      let ratio = newVal / orig;
      const maxOrig = Math.max(state.originalDims.x, state.originalDims.y, state.originalDims.z);
      if (maxOrig > 0) ratio = Math.min(ratio, MAX_BUILD_DIM / maxOrig);
      state.currentDims.x = state.originalDims.x * ratio;
      state.currentDims.y = state.originalDims.y * ratio;
      state.currentDims.z = state.originalDims.z * ratio;
      updateDimInputs();
      // Sync scale slider
      const pct = Math.round(ratio * 100);
      const slider = document.getElementById('scale-slider');
      slider.value = Math.min(pct, parseInt(slider.max));
      document.getElementById('scale-pct-display').textContent = Math.min(pct, parseInt(slider.max)) + ' %';
      updateSliderBg(slider);
    } else {
      state.currentDims[axis] = newVal;
    }
    const exceedsMax = state.currentDims.x > MAX_BUILD_DIM || state.currentDims.y > MAX_BUILD_DIM || state.currentDims.z > MAX_BUILD_DIM;
    dimError.style.display = exceedsMax ? 'block' : 'none';
    saveDimsToActiveEntry();
    updateMeshScale();
    updatePrice();
  });
});

document.getElementById('prop-lock').addEventListener('change', function() {
  state.propLocked = this.checked;
});


// ============================================================
// SCALE SLIDER
// ============================================================
function initScaleSlider() {
  const maxOrig = Math.max(state.originalDims.x, state.originalDims.y, state.originalDims.z);
  // Slider für STEP-Dateien ausblenden (keine Originaldims vorhanden)
  const sliderWrap = document.getElementById('scale-slider')?.closest('.kf-scale-row') || document.getElementById('scale-slider')?.parentElement;
  if (sliderWrap) sliderWrap.style.display = maxOrig === 0 ? 'none' : '';
  const maxPct  = maxOrig > 0 ? Math.min(Math.floor(MAX_BUILD_DIM / maxOrig * 100), 500) : 200;
  const slider  = document.getElementById('scale-slider');
  slider.max    = maxPct;
  slider.value  = 100;
  document.getElementById('scale-pct-display').textContent = '100 %';
  document.getElementById('scale-max-hint').textContent    = maxPct + ' %';
  document.getElementById('dim-error').style.display       = 'none';
  updateSliderBg(slider);
}

document.getElementById('scale-slider').addEventListener('input', function() {
  const pct   = parseInt(this.value);
  const ratio = pct / 100;
  document.getElementById('scale-pct-display').textContent = pct + ' %';
  updateSliderBg(this);
  state.currentDims.x = Math.min(state.originalDims.x * ratio, MAX_BUILD_DIM);
  state.currentDims.y = Math.min(state.originalDims.y * ratio, MAX_BUILD_DIM);
  state.currentDims.z = Math.min(state.originalDims.z * ratio, MAX_BUILD_DIM);
  updateDimInputs();
  saveDimsToActiveEntry();
  updateMeshScale();
  updatePrice();
});


// ============================================================
// INFILL SLIDER
// ============================================================
const infillSlider = document.getElementById('infill-slider');

function applyInfill(val) {
  state.infill = val;
  infillSlider.value = val;
  document.getElementById('infill-display').innerHTML = `${val} <span>%</span>`;
  updateSliderBg(infillSlider);
  // Highlight matching tick
  document.querySelectorAll('.kf-infill-ticks span').forEach(s => {
    s.classList.toggle('kf-tick-active', parseInt(s.dataset.val) === val);
  });
  updatePrice();
}

infillSlider.addEventListener('input', function() {
  let v = parseInt(this.value);
  if (v < 5) { v = 5; this.value = 5; }
  applyInfill(v);
});

// Infill-Ticks klickbar
document.querySelectorAll('.kf-infill-ticks span').forEach(span => {
  span.addEventListener('click', function() { applyInfill(parseInt(this.dataset.val)); });
});

function updateSliderBg(slider) {
  const min = parseFloat(slider.min);
  const max = parseFloat(slider.max);
  const pct = ((parseFloat(slider.value) - min) / (max - min)) * 100;
  slider.style.background = `linear-gradient(to right, var(--el-accent) ${pct}%, var(--el-border) ${pct}%)`;
}
updateSliderBg(infillSlider);


// ============================================================
// WANDSTÄRKE
// ============================================================
document.querySelectorAll('.kf-wall-btn').forEach(btn => {
  btn.addEventListener('click', function() {
    state.wallCount = parseInt(this.dataset.walls);
    document.querySelectorAll('.kf-wall-btn').forEach(b => b.classList.remove('active'));
    this.classList.add('active');
    updatePrice();
  });
});

// ============================================================
// MENGE
// ============================================================
// Qty-Steuerung erfolgt nun per File-Card in renderFileList()


// ============================================================
// EXPRESS-OPTION (Checkbox-Toggle)
// ============================================================
document.getElementById('express-check').addEventListener('change', function() {
  state.expressEnabled = this.checked;
  updatePrice();
});

// ============================================================
// KOMMENTARFELD - Zeichenzähler
// ============================================================
document.getElementById('kf-comment').addEventListener('input', function() {
  document.getElementById('kf-comment-count').textContent = this.value.length;
  state.comment = this.value;
});


// ============================================================
// PREIS-KALKULATION
//
// Pfad A - Mesh (STL/OBJ/PLY):
//   shellVol     = surfArea × 0.12 cm  (3 Perimeter × 0.4 mm)
//   interiorVol  = meshVol − shellVol
//   printVol     = shellVol + interiorVol × infill%
//   weight_g     = printVol × density × 1.15  (+ 15 % Stützstruktur-Pauschale wenn Überhänge)
//   complexSurch = baseCost × 0.10-0.15  (wenn surfArea/meshVol > 6.0)
//
// Pfad B - STEP: kein Preis → individuelles Angebot
//
// Gemeinsam (Pfad A):
//   machineCost  = printTime_h × 0.55 €/h
//   laborCost    = scaledLabor/qty  (−10%/Teil auf Platte, min 40%; −20% ab Platte 2, −25% ab Platte 3)
//   withPkg      = baseCost × 1.05
//   unitPrice    = withPkg / divisor(qty)   [mengenabhängige Marge]
//   unitPrice    = max(unitPrice, weight_g × 0.10, MIN_ABSOLUTE_PRICE)
//   express      = getExpressFee(numPlates) - plattenbasiert gestaffelt (9,99-59,99 €)
//   shipping     = 0 (wird individuell im Checkout berechnet)
// ============================================================
function calculatePrice(laborOverride = null) {
  const dims = state.currentDims;

  // Keine Daten oder STEP → kein Preis
  if (dims.x === 0 || dims.y === 0 || dims.z === 0 || !state.material) {
    return { isStep: false, weight: 0, unitPrice: 0, subtotal: 0, shipping: 0, total: 0, expressCharge: 0 };
  }
  if (state.isStepFile && (dims.x === 0 || dims.y === 0 || dims.z === 0)) {
    return { isStep: true, weight: 0, unitPrice: 0, subtotal: 0, shipping: 0, total: 0, expressCharge: 0 };
  }

  // 1. Druckvolumen (Mesh)
  let meshVol_cm3, surfArea_cm2;
  if (state.isStepFile) {
    // Bounding-Box-Näherung für STEP (kein Mesh-Parser im Browser)
    // 40% Füllgrad = typischer Mittelwert für mechanische CAD-Teile
    meshVol_cm3  = (dims.x / 10) * (dims.y / 10) * (dims.z / 10) * 0.40;
    surfArea_cm2 = 2 * (dims.x * dims.y + dims.y * dims.z + dims.x * dims.z) / 100;
  } else {
    const volScale = state.originalDims.x > 0
      ? (dims.x / state.originalDims.x) * (dims.y / state.originalDims.y) * (dims.z / state.originalDims.z)
      : 1;
    meshVol_cm3  = state.meshVolume_mm3 / 1000 * volScale;
    const surfScale    = Math.pow(Math.abs(volScale), 2 / 3);
    surfArea_cm2 = state.meshSurfaceArea_mm2 / 100 * surfScale;
  }

  // Wandstärke: 3 Perimeter × Düsendurchmesser
  const wallThickness_cm = state.wallCount * state.nozzle / 10;
  const shellVol_cm3    = Math.min(surfArea_cm2 * wallThickness_cm, meshVol_cm3);
  const interiorVol_cm3 = Math.max(meshVol_cm3 - shellVol_cm3, 0);
  const printVol_cm3    = shellVol_cm3 + interiorVol_cm3 * state.infill / 100;

  // 2. Teile pro Druckplatte
  // a) Beste achsenparallele Orientierung (0° und 90°)
  const plateW = plateFor(dims).x + CONFIG.partGap_mm;
  const plateH = plateFor(dims).y + CONFIG.partGap_mm;
  const gap    = CONFIG.partGap_mm;
  const sX = Math.max(dims.x, 1) + gap;
  const sY = Math.max(dims.y, 1) + gap;
  const grid0  = Math.floor(plateW / sX) * Math.floor(plateH / sY);
  const grid90 = Math.floor(plateW / sY) * Math.floor(plateH / sX);  // 90°-Rotation
  let gridParts = Math.max(1, Math.max(grid0, grid90));
  // Runde Teile (X ≈ Y): Diagonal-Check - passen 2 Stück über Eck?
  const isRound_ = dims.x > 0 && dims.y > 0 && Math.min(dims.x, dims.y) / Math.max(dims.x, dims.y) > 0.95;
  let diagonalHit_ = false;
  if (isRound_ && gridParts === 1) {
    const W_ = plateFor(dims).x, H_ = plateFor(dims).y;
    const dx_ = W_ - dims.x, dy_ = H_ - dims.y;
    if (dx_ > 0 && dy_ > 0 && Math.sqrt(dx_*dx_ + dy_*dy_) >= dims.x + gap * 2) { gridParts = 2; diagonalHit_ = true; }
  }

  // b) Nesting-Bonus: konkave/L-förmige/dünne Teile können ineinander
  //    geschachtelt werden → Bonus abhängig vom Füllgrad des Bounding-Box
  //    fillRatio=1 (Würfel) → kein Bonus; fillRatio→0 (dünner Haken) → bis 2×
  const boundingVol_cm3 = (dims.x * dims.y * dims.z) / 1000;
  const fillRatio   = boundingVol_cm3 > 0 ? Math.min(1, meshVol_cm3 / boundingVol_cm3) : 1;
  const isFlat_     = dims.z < 0.4 * Math.min(dims.x, dims.y);
  const nestingFactor = isFlat_ ? Math.min(1.4, 1 + Math.max(0, 0.5 - fillRatio) * 0.8) : 1.0;
  const partsPerPlate    = Math.max(1, Math.round(gridParts * nestingFactor));
  const effectivePPP     = diagonalHit_ ? 2 : Math.max(1, Math.floor(partsPerPlate * 0.9)); // Diagonal: exakt, kein Puffer
  const numPlates        = Math.ceil(state.quantity / effectivePPP);

  // 3. Materialgewicht (+15 % Stützstruktur-Pauschale wenn Überhänge erkannt)
  let weight_g = printVol_cm3 * state.material.density;
  if (state.hasOverhangs) weight_g *= 1.15;
  const materialCost = weight_g * state.material.pricePerGram;

  // 4. Druckzeit & Maschinenkosten
  const printTime_h = printVol_cm3 * state.layerHeight.timeFactor / CONFIG.volumetricFlow_cm3h;
  const machineCost = printTime_h * CONFIG.machineRate;

  // 5. Arbeitskosten: Platte 1 = voll, Platte 2 = −20 %, Platte 3+ = −25 %
  //    Skalierung: −10 % Rüstkosten je Zusatzteil auf der Platte, Minimum 40 %
  //    → partsPerPlate=1: 100 % (5,83 €), =7+: 40 % (2,33 €)
  //    Bei multi-file wird laborOverride von updatePrice() übergeben (geteilte Rüstkosten)
  let laborCost;
  if (laborOverride !== null) {
    laborCost = laborOverride;  // per-piece labor share vom combined-plate-calc
  } else {
    const scaledLabor = Math.max(
      CONFIG.laborPerPlate * 0.40,
      CONFIG.laborPerPlate * (1 - (partsPerPlate - 1) * 0.10)
    );
    const plate2count = numPlates >= 2 ? 1 : 0;
    const plate3count = Math.max(0, numPlates - 2);
    const laborTotal  = scaledLabor
      + plate2count * scaledLabor * (1 - CONFIG.extraPlateDiscount)
      + plate3count * scaledLabor * (1 - CONFIG.thirdPlateDiscount);
    laborCost = laborTotal / state.quantity;
  }

  // 6. Basis + Komplexitätszuschlag
  let baseCost = materialCost + machineCost + laborCost;
  if (meshVol_cm3 > 0) {
    const complexityRatio = surfArea_cm2 / meshVol_cm3;
    const ct = COMPLEXITY_THRESHOLDS.find(t => complexityRatio >= t.minRatio);
    if (ct) baseCost += baseCost * ct.surchargePercent;
  }

  // 7. Verpackung + mengenabhängige Marge
  const withPkg = baseCost * (1 + CONFIG.packagingSurcharge);
  const tier    = MARGIN_TIERS.find(t => state.quantity <= t.maxQty);
  let unitPrice = withPkg / tier.divisor;

  // 8. Mindestpreis
  //    Kleine Teile (<100 g) haben höheren Wertfaktor (0.14 statt 0.10)
  //    um Staffelpreise bei Leichtteilen nach unten zu begrenzen
  const weightFloorFactor = weight_g < 100 ? 0.14 : 0.10;
  const minByWeight  = weight_g * weightFloorFactor;
  const minUnitPrice = Math.max(minByWeight, MIN_ABSOLUTE_PRICE);
  unitPrice = Math.max(unitPrice, minUnitPrice);

  // 8b. Monotoniegarantie: jedes zusätzliche Stück kostet mindestens seinen
  // eigenen natürlichen Staffelpreis. total(n) >= total(n-1) + rawUnitPrice(n).
  if (state.quantity > 1 && laborOverride === null) {
    const rawUnitPrice = unitPrice;   // natürlicher Stückpreis VOR Anpassung
    const curQty = state.quantity;
    state.quantity = curQty - 1;
    const prev = calculatePrice();    // bereits monoton enforced für n-1
    state.quantity = curQty;
    const minTotal = prev.subtotal + rawUnitPrice;
    if (rawUnitPrice * curQty < minTotal) {
      unitPrice = minTotal / curQty;
    }
  }

  // 9. Gesamt (Express als Festbetrag auf Bestellebene)
  const subtotal      = unitPrice * state.quantity;
  const expressCharge = 0; // Express-Gebühr wird in updatePrice() plattenbasiert berechnet
  const shipping      = 0; // Versand wird individuell im Checkout berechnet
  const total         = subtotal + expressCharge + shipping;

  return { isStep: false, weight: weight_g, unitPrice, subtotal, shipping, total, expressCharge, partsPerPlate };
}

// Preis für eine beliebige Menge berechnen (ohne State zu verändern)
function calculatePriceForQty(qty) {
  const orig = state.quantity;
  state.quantity = qty;
  const result = calculatePrice();
  state.quantity = orig;
  return result;
}

// ============================================================
// STAFFELPREISE-TABELLE
// ============================================================
const STAFFEL_QTY = [1, 2, 3, 5, 10, 25, 50];

function updateStaffelTable() {
  const box   = document.getElementById('staffel-box');
  const tbody = document.getElementById('staffel-tbody');

  const ref = calculatePriceForQty(1);
  if (ref.total === 0) {
    box.setAttribute('hidden', '');
    return;
  }

  // Pass 1: Rohdaten + einfaches Capping auf SAVINGS_CAP
  const rows = STAFFEL_QTY.map(qty => {
    const c = calculatePriceForQty(qty);
    const rawSavings = qty > 1 && ref.unitPrice > c.unitPrice
      ? (1 - c.unitPrice / ref.unitPrice) * 100
      : 0;
    return { qty, c, rawSavings };
  });

  // Monotonie: höhere Menge → niemals weniger Ersparnis als vorherige Stufe
  let runningMax = 0;
  const savings = rows.map(r => {
    const s = r.rawSavings > 0 ? r.rawSavings : 0;
    runningMax = Math.max(runningMax, s);
    return runningMax;
  });

  // Pass 2: HTML rendern
  tbody.innerHTML = rows.map((r, i) => {
    const { qty, c } = r;
    const isActive = qty === state.quantity;
    const shipTag  = c.subtotal >= FREE_SHIPPING_THRESHOLD
      ? '<span class="kf-staffel-ship">✓ Versand frei</span>'
      : '';
    const savingsVal = savings[i];
    const badge    = savingsVal > 0
      ? `<span class="kf-staffel-savings">−${parseFloat(savingsVal.toFixed(2))} %</span>`
      : '';
    return `
      <tr class="${isActive ? 'staffel-active' : ''}" data-qty="${qty}">
        <td>${qty}×</td>
        <td>€ ${eur(c.unitPrice)}</td>
        <td>€ ${eur(c.subtotal)} ${shipTag}</td>
        <td>${badge}</td>
      </tr>`;
  }).join('');

  box.removeAttribute('hidden');

  // Klick auf Zeile → Menge übernehmen (nur bei 1 Datei)
  tbody.querySelectorAll('tr').forEach(row => {
    row.addEventListener('click', () => {
      const qty = parseInt(row.dataset.qty);
      state.quantity = qty;
      if (fileList.length === 1) {
        fileList[0].qty = qty;
        const valEl = document.getElementById('fqty-val-' + fileList[0].id);
        if (valEl) valEl.textContent = qty;
      }
      updatePrice();
    });
  });
}

// ============================================================
// PLATTEN-INFO ANZEIGE
// ============================================================
function updatePlateInfo() {
  const dims    = state.currentDims;
  const infoBox = document.getElementById('plate-info');
  const p6Box   = document.getElementById('plate-info-p6');

  // Bei mehreren Dateien ausblenden - combine hint übernimmt diese Rolle
  if (fileList.length > 1) {
    infoBox.setAttribute('hidden', '');
    if (p6Box) p6Box.setAttribute('hidden', '');
    return;
  }

  if (dims.x <= 0 || dims.y <= 0 || state.isStepFile) {
    infoBox.setAttribute('hidden', '');
    if (p6Box) p6Box.setAttribute('hidden', '');
    return;
  }

  const _plateW = plateFor(dims).x + CONFIG.partGap_mm;
  const _plateH = plateFor(dims).y + CONFIG.partGap_mm;
  const _gap    = CONFIG.partGap_mm;
  // Hybrid-Heuristik (siehe getPartsPerPlateForEntry für Erklärung)
  const _axes = [dims.x, dims.y, dims.z].slice().sort((a,b) => a - b);
  const _smin = _axes[0], _smid = _axes[1], _smax = _axes[2];
  const _isTowerLike = (_smin / _smid) > 0.7 && (_smax / _smid) > 1.5;
  const _footA = _isTowerLike ? _smin : _smid;
  const _footB = _isTowerLike ? _smid : _smax;
  const _isVeryFlat = _smin < 0.1 * _smax; // für Hinweis: kleinste Achse < 10% der größten
  const _sX = _footA + _gap;
  const _sY = _footB + _gap;
  const _grid0  = Math.floor(_plateW / _sX) * Math.floor(_plateH / _sY);
  const _grid90 = Math.floor(_plateW / _sY) * Math.floor(_plateH / _sX);
  let _gridParts = Math.max(1, Math.max(_grid0, _grid90));
  // Runde Teile (footA ≈ footB): Diagonal-Check - passen 2 Stück über Eck?
  const _isRound  = _footA > 0 && _footB > 0 && Math.min(_footA, _footB) / Math.max(_footA, _footB) > 0.95;
  let _diagonalHit = false;
  if (_isRound && _gridParts === 1) {
    const _W = plateFor(dims).x, _H = plateFor(dims).y;
    const _dx = _W - _footA, _dy = _H - _footB;
    if (_dx > 0 && _dy > 0 && Math.sqrt(_dx*_dx + _dy*_dy) >= _footA + _gap * 2) { _gridParts = 2; _diagonalHit = true; }
  }
  const _boundingVol = (_smin * _smid * _smax) / 1000;
  const _meshVol     = (state.meshVolume_mm3 || 0) / 1000;
  const _fillRatio   = _boundingVol > 0 ? Math.min(1, _meshVol / _boundingVol) : 1;
  const _isFlat      = _smin < 0.4 * _smid;
  const _nestingFactor = _isFlat ? Math.min(1.4, 1 + Math.max(0, 0.5 - _fillRatio) * 0.8) : 1.0;
  const _partsBase    = Math.max(1, Math.round(_gridParts * _nestingFactor));
  const partsPerPlate = _diagonalHit ? 2 : Math.max(1, Math.floor(_partsBase * 0.9)); // Diagonal: exakt, kein Puffer

  document.getElementById('plate-count').textContent = partsPerPlate;

  let tip = '';
  if (_isVeryFlat) {
    tip = 'Hinweis: Wir gehen davon aus, dass dieses Modell liegend gedruckt wird (Stärke nur ' + Math.round(_smin) + ' mm). Falls eine andere Druckorientierung gewünscht ist, gib das bitte in den Anmerkungen an.';
  } else if (partsPerPlate === 1) {
    tip = 'Dieses Modell nimmt eine ganze Platte in Anspruch. Die Rüstkosten fallen pro Druckplatte an.';
  } else {
    tip = `Tipp: Bestelle in Vielfachen von ${partsPerPlate} - dann teilen sich alle Teile die Rüstkosten einer Platte. Bei ${partsPerPlate + 1} Stück käme eine zweite Platte dazu.`;
  }
  document.getElementById('plate-tip').textContent = tip;
  infoBox.removeAttribute('hidden');

  // Auch in Schritt 6 anzeigen
  if (p6Box) {
    document.getElementById('plate-count-p6').textContent = partsPerPlate;
    document.getElementById('plate-tip-p6').textContent = tip;
    p6Box.removeAttribute('hidden');
  }
}

function updatePrice() {
  const animateEl = el => {
    el.classList.remove('price-updated');
    void el.offsetWidth;
    el.classList.add('price-updated');
  };

  const priceBox    = document.getElementById('price-box');
  const stepInquiry = document.getElementById('step-inquiry');
  const staffelBox  = document.getElementById('staffel-box');
  const breakdown   = document.getElementById('p-breakdown');
  const unitRow     = document.getElementById('p-unit-row');
  const pUnit       = document.getElementById('p-unit');
  const pSubtotal = document.getElementById('p-subtotal');
  const pTotal    = document.getElementById('p-total');

  // No files → hide everything
  if (fileList.length === 0) {
    priceBox.setAttribute('hidden', '');
    staffelBox.setAttribute('hidden', '');
    stepInquiry.setAttribute('hidden', '');
    return;
  }

  // STEP ohne Mesh-Daten → kein Preis möglich → Angebot (bei mehreren Dateien reicht eine)
  const allStep = fileList.some(f => f.isStep && !f.meshLoaded);
  if (allStep) {
    priceBox.setAttribute('hidden', '');
    staffelBox.setAttribute('hidden', '');
    stepInquiry.removeAttribute('hidden');
    return;
  }

  priceBox.removeAttribute('hidden');
  stepInquiry.setAttribute('hidden', '');

  let totalSubtotal = 0;
  let totalPlates   = 0;
  let entryResults;
  let shared = null;
  if (fileList.length > 1) {
    // Mehrere Dateien: gemeinsame Druckplatten und Rüstkosten
    shared = computeSharedPricing();
    entryResults  = shared.results;
    totalSubtotal = shared.subtotal;
    totalPlates   = shared.plates;
  } else {
    entryResults = fileList.map(entry => {
      const c = calculatePriceForEntry(entry);
      if (!c.isStep) {
        totalSubtotal += c.subtotal;
        if (entry.currentDims.x > 0) totalPlates += Math.ceil(entry.qty / getPartsPerPlateForEntry(entry));
      }
      return { entry, c };
    });
  }
  const expressFee   = getExpressFee(Math.max(1, totalPlates));
  const totalExpress = state.expressEnabled ? expressFee : 0;

  // Express-Badge aktualisieren
  const feeBadge = document.getElementById('express-fee-badge');
  if (feeBadge) feeBadge.textContent = '+ ' + eur(expressFee) + ' €';
  const shipping = 0; // Versand wird individuell im Checkout berechnet
  const total    = totalSubtotal + totalExpress;

  // Store combined calc in state
  const totalWeight_g_calc = entryResults.reduce((sum, { entry, c }) => sum + (c.isStep ? 0 : c.weight * entry.qty), 0);
  state.calc = { weight: totalWeight_g_calc, unitPrice: entryResults.length === 1 ? entryResults[0].c.unitPrice : 0, subtotal: totalSubtotal, shipping, total, expressCharge: totalExpress,
    plates: totalPlates,
    entries: entryResults.map(({ entry, c }) => ({ id: entry.id, unitPrice: c.unitPrice || 0, subtotal: c.subtotal || 0, weight: c.weight || 0 })) };

  const hasAnyModel = fileList.some(f => (!f.isStep || f.meshLoaded) && f.currentDims.x > 0);

  updatePriceSettingsSummary();

  if (hasAnyModel) {
    breakdown.innerHTML = '';
    const plateUtilBanner = document.getElementById('plate-util-banner');
    if (plateUtilBanner) plateUtilBanner.setAttribute('hidden', '');

    const oldDimsRow = document.getElementById('p-dims-row');
    if (oldDimsRow) oldDimsRow.remove();
    if (shared) {
      unitRow.style.display = 'none';
      breakdown.innerHTML = entryResults.map(({ entry, c }) =>
        '<div class="kf-price-breakdown-row"><span>' + esc(entry.file.name.length > 26 ? entry.file.name.substring(0, 24) + '…' : entry.file.name) + ' · ' + entry.qty + '×</span><span>€ ' + eur(c.subtotal || 0) + '</span></div>'
      ).join('');
      if (plateUtilBanner) {
        const pl = shared.plates === 1 ? '1 Druckplatte' : shared.plates + ' Druckplatten';
        plateUtilBanner.className = 'kf-plate-share';
        plateUtilBanner.innerHTML = shared.savings >= 0.1
          ? 'Alle Teile passen auf <strong>' + pl + '</strong> und teilen sich die Rüstkosten. Du sparst <strong>€ ' + eur(shared.savings) + '</strong> gegenüber einzelnen Aufträgen.'
          : 'Alle Teile zusammen brauchen <strong>' + pl + '</strong>.';
        plateUtilBanner.removeAttribute('hidden');
      }
    }
    unitRow.style.display = shared ? 'none' : '';
    const c = entryResults[0].c;
    pUnit.textContent = '€ ' + eur(c.unitPrice);
    // Maße anzeigen
    const dims = shared ? null : entryResults[0].entry.currentDims;
    if (dims && dims.x > 0) {
      const dimsRow = document.createElement('div');
      dimsRow.id = 'p-dims-row';
      dimsRow.className = 'kf-price-row';
      dimsRow.style.cssText = 'font-size:11px;color:var(--el-text-muted)';
      dimsRow.innerHTML = `<span>Maße</span><span>${dims.x.toFixed(0)} × ${dims.y.toFixed(0)} × ${dims.z.toFixed(0)} mm</span>`;
      unitRow.insertAdjacentElement('afterend', dimsRow);
    }

    // Filament-Gewicht (Summe aller Teile × Stückzahl)
    const totalWeight_g = entryResults.reduce((sum, { entry, c }) => sum + (c.isStep ? 0 : c.weight * entry.qty), 0);
    const pWeightRow = document.getElementById('p-weight-row');
    const pWeightEl  = document.getElementById('p-weight');
    // Filament-Gewicht nur intern (E-Mail/Draft Order) - nicht für Kunden sichtbar
    if (pWeightRow) pWeightRow.setAttribute('hidden', '');
    if (pWeightEl && totalWeight_g > 0) {
      pWeightEl.textContent = totalWeight_g < 1000
        ? `ca. ${Math.round(totalWeight_g)} g`
        : `ca. ${(totalWeight_g / 1000).toFixed(2)} kg`;
    }

    pSubtotal.textContent = '€ ' + eur(totalSubtotal);
    pTotal.textContent = '€ ' + eur(total);
    const shippingNote = document.getElementById('p-shipping-note');
    if (shippingNote) {
      shippingNote.innerHTML = totalSubtotal >= FREE_SHIPPING_THRESHOLD
        ? '<span class="kf-shipping-free">✓ Versandkostenfrei</span>'
        : 'Versand wird im Checkout berechnet';
    }
    animateEl(pTotal);
  } else {
    breakdown.innerHTML = '';
    const _pub = document.getElementById('plate-util-banner');
    if (_pub) _pub.setAttribute('hidden', '');
    [pUnit, pSubtotal].forEach(el => el.textContent = '-');
    pTotal.textContent = '-';
  }

  // Staffel nur bei 1 STL-Datei sinnvoll
  if (fileList.length === 1 && (!fileList[0].isStep || fileList[0].meshLoaded)) {
    // Sync single-file qty to state for staffel calc
    state.quantity = fileList[0].qty;
    updateStaffelTable();
  } else {
    staffelBox.setAttribute('hidden', '');
  }
  // Immer aufrufen - versteckt sich selbst bei mehreren Dateien
  updatePlateInfo();
}


// ============================================================
// BESTELLÜBERSICHT im Formular befüllen
// ============================================================
function updateFormSummary() {
  // File list
  const sumFilesList = document.getElementById('sum-files-list');
  sumFilesList.innerHTML = fileList.map(entry => {
    const dims = entry.currentDims;
    const dimsStr = dims.x > 0
      ? `${dims.x.toFixed(1)} × ${dims.y.toFixed(1)} × ${dims.z.toFixed(1)} mm`
      : (entry.isStep ? 'STEP-Datei' : '-');
    return `<div class="kf-summary-row"><span>${esc(entry.file.name)}</span> <strong>${entry.qty}× · ${dimsStr}</strong></div>`;
  }).join('');

  document.getElementById('sum-material').textContent = state.material.name;
  document.getElementById('sum-color').textContent    = state.color.name;
  document.getElementById('sum-layer').textContent    = state.layerHeight.label;
  document.getElementById('sum-infill').textContent   = state.infill + ' %';
  document.getElementById('sum-total').textContent = state.calc.total > 0 ? '€ ' + eur(state.calc.total) : '-';
  const sumShipping = document.getElementById('sum-shipping');
  if (sumShipping) {
    sumShipping.textContent = state.calc.total <= 0 ? '-'
      : state.calc.subtotal >= FREE_SHIPPING_THRESHOLD ? 'Kostenlos ✓'
      : 'wird im Checkout berechnet';
  }
  const commentRow = document.getElementById('sum-comment-row');
  if (state.comment && state.comment.trim()) {
    document.getElementById('sum-comment').textContent = state.comment.trim();
    commentRow.hidden = false;
  } else {
    commentRow.hidden = true;
  }
}


// ============================================================
// BESTELL-BUTTON → Formular anzeigen
// ============================================================
document.getElementById('order-btn').addEventListener('click', () => {
  if (fileList.length === 0) {
    kfError('Bitte lade zuerst eine 3D-Datei hoch.');
    return;
  }
  if (!state.material) {
    kfError('Bitte wähle ein Material aus.');
    return;
  }
  updateFormSummary();
  document.getElementById('configurator').setAttribute('hidden', '');
  document.getElementById('order-form-section').removeAttribute('hidden');
  window.scrollTo({ top: 0, behavior: 'smooth' });
});

document.getElementById('back-to-config-btn').addEventListener('click', () => {
  document.getElementById('order-form-section').setAttribute('hidden', '');
  document.getElementById('configurator').removeAttribute('hidden');
  window.scrollTo({ top: 0, behavior: 'smooth' });
});


// ============================================================
// FORMULAR: SUBMIT
// ============================================================
document.getElementById('order-form').addEventListener('submit', async function(e) {
  e.preventDefault();

  const requiredFields = ['f-firstname', 'f-lastname', 'f-email', 'f-street', 'f-zip', 'f-city', 'f-country'];
  let valid = true;

  requiredFields.forEach(id => {
    const el = document.getElementById(id);
    el.classList.remove('error');
    if (!el.value.trim()) { el.classList.add('error'); valid = false; }
  });

  // E-Mail-Format prüfen
  const emailEl  = document.getElementById('f-email');
  const emailVal = emailEl.value.trim();
  const emailOk  = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(emailVal);
  if (!emailOk) {
    emailEl.classList.add('error');
    emailEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    emailEl.focus();
    document.getElementById('form-error-msg').textContent = 'Bitte gib eine gültige E-Mail-Adresse ein.';
    document.getElementById('form-error-msg').style.display = 'block';
    return;
  }
  emailEl.classList.remove('error');

  if (!document.getElementById('f-agb').checked) {
    document.getElementById('form-error-msg').textContent = 'Bitte stimme den AGB zu.';
    document.getElementById('form-error-msg').style.display = 'block';
    return;
  }
  document.getElementById('form-error-msg').style.display = 'none';

  if (!valid) {
    document.getElementById('form-error-msg').textContent = 'Bitte fülle alle Pflichtfelder aus.';
    document.getElementById('form-error-msg').style.display = 'block';
    return;
  }

  const submitBtn = document.getElementById('submit-btn');
  const originalHTML = submitBtn.innerHTML;
  const NETLIFY_BASE = 'https://steady-puffpuff-0a7dfd.netlify.app/.netlify/functions';
  submitBtn.innerHTML = '<div class="kf-spinner"></div> Wird gesendet …';
  submitBtn.disabled = true;

  const firstName = document.getElementById('f-firstname').value.trim();
  let orderNr, orderId;

  try {
    // ── Anfragenummer vom Server holen (fortlaufend ab ANF-2610) ──────────
    const anfrResp = await fetch(`${NETLIFY_BASE}/get-anfragenr`, { method: 'POST' });
    if (!anfrResp.ok) throw new Error(`get-anfragenr HTTP ${anfrResp.status}`);
    const anfrData = await anfrResp.json();
    orderNr = anfrData.anfragenr;
    orderId = anfrData.orderId;
    var orderToken = anfrData.orderToken;
  } catch (err) {
    console.error('Anfragenummer-Fehler:', err);
    document.getElementById('form-error-msg').textContent =
      'Beim Senden ist ein Fehler aufgetreten. Bitte versuche es erneut oder schreibe uns direkt an kontakt@embeelab.de.';
    document.getElementById('form-error-msg').style.display = 'block';
    submitBtn.innerHTML = originalHTML;
    submitBtn.disabled = false;
    return;
  }

  const orderData = {
    firstName,
    orderNr,
    name:              `${firstName} ${document.getElementById('f-lastname').value.trim()}`,
    email:             document.getElementById('f-email').value.trim(),
    phone:             document.getElementById('f-phone').value.trim(),
    address: {
      street:          document.getElementById('f-street').value.trim(),
      zip:             document.getElementById('f-zip').value.trim(),
      city:            document.getElementById('f-city').value.trim(),
      country:         document.getElementById('f-country').value,
    },
    material:          state.material.name,
    color:             state.color.name,
    layerHeight_mm:    state.layerHeight.value,
    infill_pct:        state.infill,
    nozzle_mm:         state.nozzle,
    walls:             state.wallCount,
    quality:           kfState.preset ? kfPresetLabel(kfState.preset) : 'Expertenmodus',
    purpose:           kfState.purpose || '',
    files:             fileList.map(f => {
                         const r = (state.calc.entries || []).find(x => x.id === f.id) || {};
                         const d = f.currentDims;
                         return { name: f.file.name, sizeKb: Math.round(f.file.size/1024), qty: f.qty,
                                  unitPrice_eur: eur(r.unitPrice || 0), subtotal_eur: eur(r.subtotal || 0),
                                  weight_g: Math.round((r.weight || 0) * f.qty),
                                  dims: d && d.x > 0 ? d.x.toFixed(1) + ' × ' + d.y.toFixed(1) + ' × ' + d.z.toFixed(1) + ' mm' : '' };
                       }),
    plates:            state.calc.plates || 0,
    fileName:          fileList.length === 1 ? fileList[0].file.name : `${fileList.length} Dateien`,
    quantity:          fileList.reduce((s, f) => s + f.qty, 0),
    unitPrice_eur:     fileList.length === 1 ? eur(state.calc.unitPrice) : '-',
    expressBearbeitung: state.expressEnabled ? 'Ja' : 'Nein',
    expressZuschlag_eur: state.expressEnabled ? eur(state.calc.expressCharge) : '0,00',
    shippingCost_eur:  eur(state.calc.shipping),
    totalPrice_eur:    eur(state.calc.total),
    weight_g:          Math.round(state.calc.weight),
    dimensions:        fileList.length === 1 && fileList[0].currentDims.x > 0
                         ? `${fileList[0].currentDims.x.toFixed(1)} × ${fileList[0].currentDims.y.toFixed(1)} × ${fileList[0].currentDims.z.toFixed(1)} mm`
                         : '',
    volume_cm3:        state.meshVolume_mm3 > 0 ? (state.meshVolume_mm3 / 1000).toFixed(1) : '',
    scale_pct:         document.getElementById('scale-slider') ? document.getElementById('scale-slider').value : '100',
    filament_g:        Math.round(state.calc.weight),
    notes:             document.getElementById('f-notes').value.trim(),
    bundleRef:         document.getElementById('f-bundle-ref').value.trim() ? 'ANF' + document.getElementById('f-bundle-ref').value.trim() : '',
    referredBy:        document.getElementById('f-ref') ? document.getElementById('f-ref').value.trim() : '',
  };

  orderData.subject = orderData.bundleRef
    ? `[BÜNDELUNG → ${orderData.bundleRef}] ${orderData.fileName} · ${orderData.quantity}× ${orderData.material} · € ${orderData.totalPrice_eur}`
    : `[Konfigurator] ${orderData.fileName} · ${orderData.quantity}× ${orderData.material} · € ${orderData.totalPrice_eur}`;

  // ── Nachrichtentext ─────────────────────────────────────────────────────
  const nozzleLabel = state.nozzle === 0.2 ? 'Ø 0.2 mm (Präzision)' : 'Ø 0.4 mm (Standard)';
  const msgBody = [
    '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
    `NEUE 3D-DRUCK-ANFRAGE - embeeLAB Konfigurator`,
    `Anfragenummer: ${orderNr}`,
    orderData.bundleRef ? `⚡ BÜNDELUNG mit: ${orderData.bundleRef}` : '',
    '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
    '',
    '── KUNDENDATEN ──────────────────────────',
    `Name:           ${orderData.name}`,
    `E-Mail:         ${orderData.email}`,
    `Telefon:        ${orderData.phone || '-'}`,
    `Adresse:        ${orderData.address.street}, ${orderData.address.zip} ${orderData.address.city}, ${orderData.address.country}`,
    '',
    '── DATEIEN ──────────────────────────────',
    ...fileList.map((entry, i) => {
      const dims = entry.currentDims;
      const dimsStr = dims.x > 0
        ? `${dims.x.toFixed(1)}×${dims.y.toFixed(1)}×${dims.z.toFixed(1)} mm`
        : (entry.isStep ? 'STEP' : '-');
      const r = (state.calc.entries || []).find(x => x.id === entry.id) || {};
      return `Datei ${i+1}: ${entry.file.name} | ${Math.round(entry.file.size/1024)} KB | ${entry.qty}× | ${dimsStr} | € ${eur(r.unitPrice || 0)}/Stk = € ${eur(r.subtotal || 0)}`;
    }),
    '',
    '── KONFIGURATION ────────────────────────',
    `Material:       ${orderData.material}`,
    `Farbe:          ${orderData.color}`,
    `Düse:           ${nozzleLabel}`,
    `Schichtdicke:   ${orderData.layerHeight_mm} mm`,
    `Infill:         ${orderData.infill_pct} %`,
    `Wände:          ${state.wallCount}`,
    `Qualität:       ${kfState.preset ? kfPresetLabel(kfState.preset) : 'Expertenmodus'}`,
    `Verwendung:     ${kfState.purpose || '-'}`,
    `Express:        ${orderData.expressBearbeitung}`,
    '',
    '── PREISKALKULATION ─────────────────────',
    `Stückpreis:     € ${orderData.unitPrice_eur}`,
    fileList.length > 1 ? `Druckplatten:   ${state.calc.plates || '-'} (gemeinsame Rüstkosten)` : '',
    `Zwischensumme:  € ${eur(state.calc.subtotal)}`,
    `Versand:        ${state.calc.subtotal >= FREE_SHIPPING_THRESHOLD ? 'Kostenlos' : 'wird im Checkout berechnet'}`,
    state.expressEnabled ? `Express-Zuschlag: € ${orderData.expressZuschlag_eur}` : '',
    `GESAMT:         € ${orderData.totalPrice_eur}`,
    '',
    '── ANMERKUNGEN ──────────────────────────',
    (state.comment && state.comment.trim()) ? state.comment.trim() : '-',
    '',
    (document.getElementById('f-notes').value.trim()
      ? '── WEITERE HINWEISE (Formular) ──────────\n' + document.getElementById('f-notes').value.trim()
      : ''),
    '',
    (orderData.referredBy
      ? '── EMPFEHLUNG ───────────────────────────\nEmpfohlen durch: ' + orderData.referredBy
      : ''),
    '',
    '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
  ].filter(l => l !== undefined).join('\n');

  // ── Direkter R2-Upload (via Presigned URL, kein Größenlimit) ─────────────
  const MAX_RETRIES = 3;

  async function uploadFileToR2(file, orderId, bundleRef, onProgress) {
    // 1. Presigned URL von Netlify Function holen
    const urlResp = await fetch(`${NETLIFY_BASE}/get-upload-url`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ orderId, filename: file.name, bundleRef: bundleRef || '' }),
    });
    if (!urlResp.ok) {
      const err = await urlResp.json().catch(() => ({}));
      throw new Error(err.error || 'Upload-URL konnte nicht erstellt werden');
    }
    const { uploadUrl } = await urlResp.json();

    // 2. Datei direkt zu Cloudflare R2 hochladen (PUT mit Retry)
    let lastError;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const xhr = new XMLHttpRequest();
        await new Promise((resolve, reject) => {
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable && onProgress) {
              onProgress(Math.round((e.loaded / e.total) * 100));
            }
          });
          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error(`Upload fehlgeschlagen (HTTP ${xhr.status})`));
          });
          xhr.addEventListener('error', () => reject(new Error('Netzwerkfehler beim Upload')));
          xhr.addEventListener('abort', () => reject(new Error('Upload abgebrochen')));
          xhr.open('PUT', uploadUrl);
          xhr.setRequestHeader('Content-Type', 'application/octet-stream');
          xhr.send(file);
        });
        lastError = null;
        break;
      } catch (e) {
        lastError = e;
        if (attempt < MAX_RETRIES - 1) await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
      }
    }
    if (lastError) throw lastError;
  }

  try {
    // ── Dateien hochladen (direkt zu R2, kein Größenlimit) ─────────────────
    submitBtn.innerHTML = '<span class="kf-spinner"></span> Datei wird hochgeladen…';

    for (const entry of fileList) {
      await uploadFileToR2(entry.file, orderId, orderData.bundleRef, (pct) => {
        submitBtn.innerHTML = `<span class="kf-spinner"></span> Upload ${pct}%…`;
      });
    }

    submitBtn.innerHTML = '<span class="kf-spinner"></span> Anfrage wird gesendet…';

    // ── Netlify Function: Bestelldaten senden (ohne Datei) ─────────────────
    const nlResp = await fetch(`${NETLIFY_BASE}/send-order`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ orderData, msgBody, orderToken }),
    });

    if (!nlResp.ok) {
      const err = await nlResp.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${nlResp.status}`);
    }

    // Hinweis: Hier stand ein Backup-Eintrag per fetch('/contact'). Der funktioniert nicht mehr -
    // Shopifys Bot-Schutz lehnt programmatische POSTs mit "Missing CAPTCHA token" (HTTP 400) ab,
    // nur nativ gerenderte Formulare kommen durch. Durch das angehaengte .catch() blieb das
    // unbemerkt. Am 31.08.2026 entfernt. Fuer eine serverseitige Zweitablage waere der Weg ueber
    // Netlify Blobs zu gehen (Muster: widerruf.js im Chatbot-Repo).

    // ── Shopify Draft Order erstellen → zur Kasse weiterleiten ────────────
    submitBtn.innerHTML = '<span class="kf-spinner"></span> Weiterleitung zur Kasse…';

    const draftResp = await fetch(`${NETLIFY_BASE}/create-draft-order`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ orderData, anfragenr: orderNr, orderToken }),
    });

    if (draftResp.ok) {
      const draftData = await draftResp.json();
      if (draftData.invoiceUrl) {
        window.location.href = draftData.invoiceUrl;
        return;
      }
    }

    // Fallback: Erfolgsseite anzeigen falls Weiterleitung fehlschlägt
    showSuccess(orderData.email, orderNr);

  } catch (err) {
    console.error('Submit-Fehler:', err);
    document.getElementById('form-error-msg').textContent =
      'Beim Senden ist ein Fehler aufgetreten. Bitte versuche es erneut oder schreibe uns direkt an kontakt@embeelab.de.';
    document.getElementById('form-error-msg').style.display = 'block';
    submitBtn.innerHTML = originalHTML;
    submitBtn.disabled = false;
  }
});


// ============================================================
// SUCCESS SCREEN
// ============================================================
function showSuccess(email, orderNr) {
  kfTrack('bestellt');
  document.getElementById('order-form-section').setAttribute('hidden', '');
  document.getElementById('success-email').textContent = email;
  document.getElementById('success-order-nr').textContent = orderNr;
  // Anfragenummer für Bündelung merken
  sessionStorage.setItem('embeelab_bundle_ref', orderNr);
  document.getElementById('success-screen').removeAttribute('hidden');
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

// "Noch ein Modell bestellen" - mit Bündelungs-Referenz
document.getElementById('new-order-bundle-btn').addEventListener('click', () => {
  resetFile();
  resetForm();
  // Referenzfeld automatisch ausfüllen
  const ref = sessionStorage.getItem('embeelab_bundle_ref');
  if (ref) document.getElementById('f-bundle-ref').value = ref.replace(/^ANF-?/i, '');
  document.getElementById('success-screen').setAttribute('hidden', '');
  document.getElementById('configurator').removeAttribute('hidden');
  window.scrollTo({ top: 0, behavior: 'smooth' });
});

// "Neue Anfrage (ohne Bündelung)"
document.getElementById('new-order-btn').addEventListener('click', () => {
  resetFile();
  resetForm();
  sessionStorage.removeItem('embeelab_bundle_ref');
  document.getElementById('success-screen').setAttribute('hidden', '');
  document.getElementById('configurator').removeAttribute('hidden');
  window.scrollTo({ top: 0, behavior: 'smooth' });
});

function resetForm() {
  document.getElementById('order-form').reset();
  document.getElementById('form-error-msg').style.display = 'none';
}


// ============================================================
// HILFSFUNKTIONEN
// ============================================================
function eur(n) {
  return typeof n === 'number'
    ? n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '-';
}

function formatFileSize(bytes) {
  if (bytes < 1024)        return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}


// ============================================================
// WIZARD NAVIGATION
// ============================================================
let wizardStep = 1;

function goToStep(n) {
  wizardStep = n;
  const grid    = document.getElementById('kf-grid');
  const content = document.getElementById('wizard-content');

  if (n === 1) {
    grid.classList.add('kf-grid--step1');
    content.setAttribute('hidden', '');
    refreshPane1Nav();
  } else {
    grid.classList.remove('kf-grid--step1');
    content.removeAttribute('hidden');
    // Panes 2-6 → data-step maps to index n-2
    document.querySelectorAll('.kf-wizard-pane').forEach(pane => {
      const paneStep = parseInt(pane.id.replace('pane-', ''));
      pane.classList.toggle('kf-pane-active', paneStep === n);
    });
    refreshPane1Nav();
    // Recalculate price when reaching step 6
    if (n === 6) updatePrice();
  }

  // Update stepper dots
  document.querySelectorAll('.kf-stepper-step').forEach(dot => {
    const s = parseInt(dot.dataset.step);
    dot.classList.remove('kf-active', 'kf-done');
    if (s === n)  dot.classList.add('kf-active');
    if (s < n)    dot.classList.add('kf-done');
  });

  // Zum Anfang des neuen Schritts scrollen. Der Stepper klebt oben fest, seine Position
  // taugt deshalb nicht als Ziel; wir nehmen den Inhalt und ziehen die Stepper-Höhe ab.
  const stepper = document.getElementById('kf-stepper');
  const anchor  = n === 1 ? grid : content;
  if (anchor) {
    const offset = (stepper ? stepper.offsetHeight : 0) + 12;
    const targetNow = () => Math.max(0, anchor.getBoundingClientRect().top + window.scrollY - offset);
    window.scrollTo({ top: targetNow(), behavior: 'smooth' });
    // Absicherung: Die 3D-Vorschau kann kurz danach noch wachsen, und manche Browser lassen
    // die weiche Animation aus. Dann Ziel neu berechnen und direkt hinspringen.
    setTimeout(() => { const t = targetNow(); if (Math.abs(window.scrollY - t) > 40) window.scrollTo(0, t); }, 700);
  }
  kfClearError();
  if (n === 4) kfQualityRefresh();
  if (n === 6) kfTrack('preis');
  kfLiveBar();
}

// Stepper-Dots: bereits besuchte Schritte sind klickbar
document.querySelectorAll('.kf-stepper-step').forEach(dot => {
  dot.style.cursor = 'pointer';
  dot.addEventListener('click', function() {
    const target = parseInt(this.dataset.step);
    if (target === wizardStep) return;
    // Nur erreichbare Schritte: vorwärts nur wenn Datei geladen, rückwärts immer
    if (target > wizardStep) {
      // Vorwärts: nur wenn Voraussetzungen erfüllt
      if (!state.file) return;
      if (target >= 4 && !state.material) return;
    }
    if (target === 1) { resetFile(); return; }
    goToStep(target);
  });
});

// Step-1 Weiter-Button
document.getElementById('pane1-weiter-btn').addEventListener('click', () => {
  if (wizardStep === 6) { goToStep(2); return; }
  if (fileList.length === 0) { kfError('Bitte lade zuerst eine 3D-Datei hoch.'); return; }
  goToStep(2); // STEP: Maßeingabe in Step 2, STL: Maße aus Mesh
});

// All "Zurück / Weiter" nav buttons (data-goto attribute)
document.querySelectorAll('.kf-step-nav-btn[data-goto]').forEach(btn => {
  btn.addEventListener('click', function() {
    const target = parseInt(this.dataset.goto);
    // Validate material before leaving step 4
    if (wizardStep === 3 && target === 4 && !state.material) {
      kfError('Bitte wähle ein Material aus.');
      return;
    }
    // "Zurück" from step 2 → reset file
    if (wizardStep === 2 && target === 1) {
      resetFile();
      return;
    }
    goToStep(target);
  });
});



// ============================================================
// EINFACHER MODUS, VERWENDUNGSZWECK, PREISLEISTE, HINWEISE
// ============================================================
const kfState = { mode: 'simple', preset: 'standard', purpose: '' };

// Nutzungszähler: nur Tagessummen bei Netlify, keine Cookies, keine Kennung.
// Jedes Ereignis zählt höchstens einmal pro Seitenaufruf. Fehler sind egal.
const kfTracked = new Set();
function kfTrack(ev) {
  if (kfTracked.has(ev)) return;
  kfTracked.add(ev);
  try {
    fetch('https://steady-puffpuff-0a7dfd.netlify.app/.netlify/functions/konf-stats', { method: 'POST', body: ev, keepalive: true, mode: 'no-cors' }).catch(() => {});
  } catch (e) {}
}
const KF_PRESETS = {
  standard: { label: 'Standard',          layer: 0.20, infill: 15, walls: 2 },
  fein:     { label: 'Feine Oberfläche',  layer: 0.12, infill: 15, walls: 2 },
  stabil:   { label: 'Besonders stabil',  layer: 0.20, infill: 40, walls: 4 },
};
function kfPresetLabel(key) { return (KF_PRESETS[key] || {}).label || key; }

// Hinweis statt Browser-Popup: im Upload-Bereich (Schritt 1) oder oben im aktiven Schritt
function kfError(msg) {
  kfClearError();
  const box = document.createElement('div');
  box.className = 'kf-inline-error';
  box.setAttribute('role', 'alert');
  box.innerHTML = '<span>' + esc(String(msg)).replace(/\n/g, '<br>') + '</span><button type="button" aria-label="Hinweis schließen">&times;</button>';
  box.querySelector('button').addEventListener('click', kfClearError);
  let host = null;
  if (typeof wizardStep === 'undefined' || wizardStep === 1) {
    const zone = document.getElementById('upload-zone');
    host = zone ? zone.parentNode : null;
    if (host) host.insertBefore(box, zone.nextSibling);
  } else {
    host = document.querySelector('.kf-wizard-pane.kf-pane-active');
    if (host) host.insertBefore(box, host.firstChild);
  }
  if (!host) { document.body.appendChild(box); box.classList.add('kf-inline-error--float'); }
  box.scrollIntoView({ behavior: 'smooth', block: 'center' });
}
function kfClearError() { document.querySelectorAll('.kf-inline-error').forEach(e => e.remove()); }

// Voreinstellung anwenden: nutzt dieselben Wege wie ein Klick im Expertenmodus
function kfApplyPreset(key) {
  const pr = KF_PRESETS[key];
  if (!pr) return;
  kfState.preset = key;
  kfState.applying = true;
  if (state.nozzle !== 0.4) {
    const b04 = document.querySelector('.kf-nozzle-btn[data-nozzle="0.4"]');
    if (b04) b04.click();
  }
  const available = LAYER_HEIGHTS.filter(lh => lh.nozzle === state.nozzle);
  const idx = available.findIndex(lh => lh.value === pr.layer);
  const slider = document.getElementById('layer-slider');
  if (slider && idx >= 0) { slider.value = idx; slider.dispatchEvent(new Event('input')); }
  applyInfill(pr.infill);
  const wb = document.querySelector('.kf-wall-btn[data-walls="' + pr.walls + '"]');
  if (wb) wb.click();
  kfState.applying = false;
  document.querySelectorAll('.kf-preset').forEach(b => b.classList.toggle('active', b.dataset.preset === key));
  kfLiveBar();
}

function kfSetMode(mode) {
  const isPla = state.material && state.material.id === 'pla';
  if (!isPla) mode = 'expert';
  kfState.mode = mode;
  document.querySelectorAll('.kf-mode-btn').forEach(b => {
    b.classList.toggle('active', b.dataset.mode === mode);
    if (b.dataset.mode === 'simple') b.disabled = !isPla;
  });
  document.getElementById('kf-presets').hidden = mode !== 'simple';
  document.getElementById('kf-expert').hidden  = mode !== 'expert';
  const note = document.getElementById('kf-quality-note');
  if (!isPla && state.material) {
    note.textContent = 'Für ' + state.material.name + ' stellst du Schichtdicke, Füllung und Wände selbst ein. Die Vorgaben sind gute Startwerte.';
    note.hidden = false;
  } else if (mode === 'expert') {
    note.textContent = 'Alle Einstellungen im Detail. Mit „Einfach“ kommst du zurück zu den drei Stufen.';
    note.hidden = false;
  } else {
    note.hidden = true;
  }
  if (mode === 'expert') kfState.preset = null;
  else kfApplyPreset(kfState.preset || 'standard');
}
function kfQualityRefresh() { kfSetMode(state.material && state.material.id === 'pla' ? (kfState.mode === 'expert' && kfState.touchedExpert ? 'expert' : 'simple') : 'expert'); }

document.querySelectorAll('.kf-mode-btn').forEach(b => b.addEventListener('click', function () {
  if (this.disabled) return;
  if (this.dataset.mode === 'expert') kfState.touchedExpert = true;
  if (this.dataset.mode === 'simple') { kfState.touchedExpert = false; kfState.preset = kfState.preset || 'standard'; }
  kfSetMode(this.dataset.mode);
}));
document.querySelectorAll('.kf-preset').forEach(b => b.addEventListener('click', function () { kfApplyPreset(this.dataset.preset); }));
// Wer im Expertenmodus selbst etwas ändert, verlässt die Voreinstellung
document.getElementById('kf-expert').addEventListener('input', () => { if (!kfState.applying) kfState.preset = null; });
document.getElementById('kf-expert').addEventListener('click', e => { if (!kfState.applying && e.target.closest('button,[data-idx],[data-val]')) kfState.preset = null; });

// Verwendungszweck -> Materialempfehlung
const KF_PURPOSE_HINTS = {
  pla:  'Empfehlung: PLA. Präzise, viele Farben, günstig.',
  petg: 'Empfehlung: PETG. Zäh und feuchtigkeitsbeständig.',
  asa:  'Empfehlung: ASA. UV- und wetterbeständig.',
  abs:  'Empfehlung: ABS. Hitze- und schlagfest.',
  tpu:  'Empfehlung: TPU. Flexibel und gummiartig.',
};
document.querySelectorAll('.kf-purpose-btn').forEach(b => b.addEventListener('click', function () {
  document.querySelectorAll('.kf-purpose-btn').forEach(x => x.classList.toggle('active', x === this));
  kfState.purpose = this.dataset.purpose;
  selectMaterial(this.dataset.mat);
  const hint = document.getElementById('kf-purpose-hint');
  hint.textContent = this.dataset.purpose === 'Weiß ich nicht'
    ? 'Wir starten mit PLA und prüfen deine Datei. Passt ein anderes Material besser, melden wir uns vor dem Druck.'
    : KF_PURPOSE_HINTS[this.dataset.mat] + ' Unten kannst du auch ein anderes Material wählen.';
  hint.hidden = false;
  kfClearError();
}));

// Preisleiste ab Schritt 2, damit jede Änderung sofort sichtbar ist
const kfBar = document.createElement('div');
kfBar.className = 'kf-livebar';
kfBar.hidden = true;
kfBar.setAttribute('aria-live', 'polite');
kfBar.innerHTML = '<div class="kf-livebar-inner"><span class="kf-livebar-meta" id="kf-livebar-meta"></span><span class="kf-livebar-price" id="kf-livebar-price"></span></div>';
document.body.appendChild(kfBar);
function kfLiveBar() {
  const step = typeof wizardStep === 'undefined' ? 1 : wizardStep;
  if (step < 2 || step > 5 || fileList.length === 0) { kfBar.hidden = true; return; }
  kfBar.hidden = false;
  const meta  = document.getElementById('kf-livebar-meta');
  const price = document.getElementById('kf-livebar-price');
  const allStep = fileList.every(f => f.isStep && !f.meshLoaded);
  if (allStep) { meta.textContent = 'STEP-Datei'; price.textContent = 'Preis auf Anfrage'; return; }
  if (!state.material) { meta.textContent = 'Preis erscheint nach der Materialwahl'; price.textContent = ''; return; }
  const qty = fileList.reduce((sum, f) => sum + f.qty, 0);
  const parts = [state.material.name, qty + ' Stück'];
  if (kfState.preset && state.material.id === 'pla') parts.push(kfPresetLabel(kfState.preset));
  meta.textContent = parts.join(' · ');
  price.innerHTML = 'ca. ' + eur(state.calc.total) + ' €<small>zzgl. Versand</small>';
}
const _kfUpdatePrice = updatePrice;
updatePrice = function () { _kfUpdatePrice.apply(this, arguments); kfLiveBar(); };

// Materialwechsel: im Qualitätsschritt den passenden Modus zeigen
const _kfSelectMaterial = selectMaterial;
selectMaterial = function (id) {
  _kfSelectMaterial(id);
  if (wizardStep === 4) kfQualityRefresh();
  kfLiveBar();
};

// ============================================================
// START
// ============================================================
buildMaterialGrid();
buildColorGrid();
buildLayerGrid();
updatePrice();
kfTrack('geoeffnet');
